/**
 * Theme JS - Core Interactivity for Custom Shopify Theme
 */

document.addEventListener('DOMContentLoaded', () => {
  initMobileMenu();
  initHeaderSelectors();
  initCartDrawer();
  initCartAjaxHandlers();
  initProductPageBuyButtons();
  initSearchModal();
  initMainSearchPage();
  initBackToTop();
  initMobileFooterAccordions();
  initProductCards();
  initAutomaticDiscountDisplays();
  initAutomaticDiscountObserver();
  initAutomaticDiscountSchedule();
  initCollectionFilters();
  initMainProductWishlist();
  initWishlistDrawer();
  initCartCoupon();

  if (window.themeWishlist) {
    window.themeWishlist.updateUI();
  }

  // Track Collection Page Context for Breadcrumbs
  if (window.location.pathname.startsWith('/collections/') && !window.location.pathname.includes('/products/')) {
    const titleEl = document.querySelector('.collection-main-title') || document.querySelector('h1');
    if (titleEl) {
      sessionStorage.setItem('last_collection_url', window.location.pathname + window.location.search);
      sessionStorage.setItem('last_collection_title', titleEl.textContent.trim());
    }
  }
});

/**
 * Initializes the mobile menu drawer and teleportation
 */
function initMobileMenu() {
  const drawer = document.getElementById('MobileNavDrawer');
  const overlay = document.querySelector('.mobile-drawer-overlay');

  if (!drawer) return;

  // Clean up any existing duplicate drawer elements in body
  const existingDrawers = document.querySelectorAll('body > .mobile-navigation-drawer');
  existingDrawers.forEach(d => {
    if (d !== drawer) d.remove();
  });

  const existingOverlays = document.querySelectorAll('body > .mobile-drawer-overlay');
  existingOverlays.forEach(o => {
    if (o !== overlay) o.remove();
  });

  // Teleport drawer and overlay to body to escape the header's CSS transform containing block
  if (overlay && overlay.parentNode !== document.body) {
    document.body.appendChild(overlay);
  }
  if (drawer && drawer.parentNode !== document.body) {
    document.body.appendChild(drawer);
  }
}

// Helper to close mobile menu
const closeMobileMenu = () => {
  const drawer = document.getElementById('MobileNavDrawer');
  const overlay = document.querySelector('.mobile-drawer-overlay');
  if (drawer) {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    const hamburger = document.querySelector('.mobile-menu-toggle');
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
  }
  if (overlay) overlay.classList.remove('is-active');
  document.body.style.overflow = '';
};

// Global delegated event listeners for mobile navigation drawer, cart drawer, and wishlist drawer
document.addEventListener('click', (e) => {
  // 1. Toggle Open Mobile Nav
  const toggleBtn = e.target.closest('.mobile-menu-toggle');
  if (toggleBtn) {
    e.preventDefault();
    const drawer = document.getElementById('MobileNavDrawer');
    const overlay = document.querySelector('.mobile-drawer-overlay');
    if (drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
    }
    if (overlay) overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    return;
  }

  // 2. Toggle Close Mobile Nav
  const closeBtn = e.target.closest('.mobile-drawer-close');
  const overlayClick = e.target.matches('.mobile-drawer-overlay');
  if (closeBtn || overlayClick) {
    e.preventDefault();
    closeMobileMenu();
    return;
  }

  // 3. Open Wishlist Drawer
  const wishlistTrigger = e.target.closest('#HeaderWishlistTrigger, #MobileWishlistTrigger, .hamburger-wishlist-trigger');
  if (wishlistTrigger) {
    e.preventDefault();
    const wishlistDrawer = document.getElementById('WishlistDrawer');
    if (wishlistDrawer) {
      closeMobileMenu();
      wishlistDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    return;
  }

  // 4. Open Cart Drawer
  const cartTrigger = e.target.closest('.cart-badge-container, .cart-drawer-trigger');
  if (cartTrigger) {
    e.preventDefault();
    const cartDrawer = document.getElementById('CartDrawer');
    const recPanel = document.getElementById('CartDrawerRecs');
    if (cartDrawer) {
      closeMobileMenu();
      cartDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (recPanel) recPanel.classList.remove('is-closed');
    }
    return;
  }

  // 5. Submenu Toggle Accordion
  const submenuToggle = e.target.closest('.mobile-submenu-toggle-btn');
  if (submenuToggle) {
    e.preventDefault();
    const parent = submenuToggle.closest('.mobile-drawer-menu-item');
    const list = parent ? parent.querySelector('.mobile-drawer-submenu-list') : null;
    const plusIcon = submenuToggle.querySelector('.toggle-icon-plus');

    if (list) {
      const isOpen = list.classList.contains('is-open');
      if (isOpen) {
        list.classList.remove('is-open');
        if (plusIcon) plusIcon.textContent = '+';
      } else {
        list.classList.add('is-open');
        if (plusIcon) plusIcon.textContent = '−';
      }
      submenuToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    return;
  }

  // 6. Grandchild Submenu Toggle Accordion
  const grandchildToggle = e.target.closest('.mobile-grandchild-toggle-btn');
  if (grandchildToggle) {
    e.preventDefault();
    const parent = grandchildToggle.closest('.mobile-drawer-submenu-item');
    const list = parent ? parent.querySelector('.mobile-drawer-grandchild-list') : null;
    const chevron = grandchildToggle.querySelector('.toggle-chevron');

    if (list) {
      const isOpen = list.classList.contains('is-open');
      if (isOpen) {
        list.classList.remove('is-open');
        if (chevron) chevron.classList.remove('is-open');
      } else {
        list.classList.add('is-open');
        if (chevron) chevron.classList.add('is-open');
      }
      grandchildToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    return;
  }
});

// Close drawer on Escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    closeMobileMenu();
  }
});

// Shopify Theme Editor section load listener to re-teleport header drawer elements
document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId === 'header') {
    initMobileMenu();
  }
});

/**
 * Initializes the Language and Currency selector interactions
 */
function initHeaderSelectors() {
  const dropdownTriggers = document.querySelectorAll('.header-dropdown-trigger');

  dropdownTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const name = trigger.textContent.trim().split(' ')[0];
      // For now, showcase a premium native browser feedback or custom modal.
      // In Shopify production, these are connected to currency/language forms.
      console.log(`Dropdown triggered for: ${name}`);
    });
  });
}

/**
 * Initializes the interactive Cart Drawer (Double-Panel layout)
 */
function initCartDrawer() {
  const cartDrawer = document.getElementById('CartDrawer');
  const cartClose = document.querySelector('.cart-drawer-close');
  const cartOverlay = document.querySelector('.cart-drawer-overlay');
  const continueShopping = document.querySelector('.continue-shopping-link');

  // Recommendations slide-out trigger
  const recClose = document.querySelector('.recommendations-close');
  const recPanel = document.getElementById('CartDrawerRecs');

  if (!cartDrawer) return;

  // Open Cart Drawer
  const cartTriggers = document.querySelectorAll('.cart-badge-container, .cart-drawer-trigger');
  cartTriggers.forEach(trigger => {
    if (trigger.dataset.drawerInitialized) return;
    trigger.dataset.drawerInitialized = 'true';
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      cartDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden'; // Lock background scrolling
      // Ensure the recommendations panel is reset to visible when re-opening
      if (recPanel) recPanel.classList.remove('is-closed');
    });
  });

  // Close Cart Drawer
  const closeDrawer = () => {
    cartDrawer.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  if (cartClose) cartClose.addEventListener('click', closeDrawer);
  if (cartOverlay) cartOverlay.addEventListener('click', closeDrawer);
  if (continueShopping) {
    continueShopping.addEventListener('click', (e) => {
      e.preventDefault();
      closeDrawer();
    });
  }

  // Close recommendations panel independently
  if (recClose && recPanel) {
    recClose.addEventListener('click', (e) => {
      e.preventDefault();
      recPanel.classList.add('is-closed');
    });
  }

  // Utility tabs toggle (Note, Coupon, Gift Wrap)
  const tabButtons = cartDrawer.querySelectorAll('.cart-drawer-tab-btn');
  const addonPanels = cartDrawer.querySelectorAll('.cart-drawer-addon-panel');

  tabButtons.forEach(btn => {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      const tabId = this.getAttribute('data-tab');
      const panel = cartDrawer.querySelector('#AddonPanel-' + tabId);
      if (!panel) return;

      const isAlreadyActive = this.classList.contains('is-active');

      tabButtons.forEach(b => b.classList.remove('is-active'));
      addonPanels.forEach(p => p.classList.remove('is-active'));

      if (!isAlreadyActive) {
        this.classList.add('is-active');
        panel.classList.add('is-active');
      }
    });
  });
}

/**
 * Initializes the premium Centered Search Modal
 */
/**
 * Initializes the premium Centered Search Modal or Search Drawer based on settings and device
 */
function initSearchModal() {
  const triggers = document.querySelectorAll('.search-modal-trigger');
  const modal = document.getElementById('SearchModal');
  const modalOverlay = document.querySelector('.search-modal-overlay');
  const modalCloseBtn = document.querySelector('.search-modal-close');
  const modalInput = document.querySelector('.search-modal-input');

  const drawer = document.getElementById('SearchDrawer');
  const drawerOverlay = document.querySelector('.search-drawer-overlay');
  const drawerCloseBtn = document.querySelector('.search-drawer-close');
  const drawerInput = document.querySelector('.search-drawer-input');

  if (!drawer) return;

  // Teleport overlay y drawer al body para escapar del CSS transform del header
  if (modalOverlay) document.body.appendChild(modalOverlay);
  // El modal va DENTRO del overlay — así el overlay fixed es el scroll container
  if (modal && modalOverlay) modalOverlay.appendChild(modal);
  if (drawerOverlay) document.body.appendChild(drawerOverlay);
  document.body.appendChild(drawer);

  const desktopSearchType = drawer.getAttribute('data-desktop-search-type') || 'popup';
  const isMobile = () => window.innerWidth <= 1024;

  // Search History Helper Functions
  const getSearchHistory = () => {
    try {
      return JSON.parse(localStorage.getItem('theme_search_history') || '[]');
    } catch (e) {
      return [];
    }
  };

  const updateRecentSearchesUI = () => {
    const history = getSearchHistory();
    const rootUrl = window.Shopify?.routes?.root || '/';
    const rootClean = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';

    const html = history.length > 0 ? `
      <div class="search-recent-history-section" style="text-align: left;">
        <h3 class="search-modal-section-title search-drawer-section-title">Búsquedas recientes</h3>
        <div class="search-modal-keywords-list search-drawer-keywords-list" style="display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 0.5rem; justify-content: flex-start;">
          ${history.map(term => `<a href="${rootClean}search?q=${encodeURIComponent(term)}&type=product" class="search-keyword-pill search-suggestion-pill">${escapeSearchHtml(term)}</a>`).join('')}
        </div>
      </div>
    ` : '';

    if (modal) {
      const modalDefault = modal.querySelector('.search-modal-default-content');
      if (modalDefault) {
        let historyContainer = modalDefault.querySelector('.search-recent-history-container');
        if (!historyContainer) {
          historyContainer = document.createElement('div');
          historyContainer.className = 'search-recent-history-container';
          modalDefault.insertBefore(historyContainer, modalDefault.firstChild);
        }
        historyContainer.innerHTML = html;
      }
    }

    if (drawer) {
      const drawerDefault = drawer.querySelector('.search-drawer-default-content');
      if (drawerDefault) {
        let historyContainer = drawerDefault.querySelector('.search-recent-history-container');
        if (!historyContainer) {
          historyContainer = document.createElement('div');
          historyContainer.className = 'search-recent-history-container';
          drawerDefault.insertBefore(historyContainer, drawerDefault.firstChild);
        }
        historyContainer.innerHTML = html;
      }
    }
  };

  // Open handler
  const openSearch = (e) => {
    if (e) e.preventDefault();
    closeSearch(); // Close any currently open search container first
    updateRecentSearchesUI(); // Refresh history list

    if (isMobile() || desktopSearchType === 'drawer') {
      // Open Search Drawer (panel lateral) — bloquea body scroll
      drawer.classList.add('is-open');
      if (drawerOverlay) drawerOverlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (drawerInput) drawerInput.focus();
      }, 150);
    } else {
      // Open Search Modal Popup — el overlay fixed cubre la pantalla y hace scroll
      if (modal) {
        modal.classList.add('is-open');
        if (modalOverlay) {
          modalOverlay.classList.add('is-active');
          modalOverlay.scrollTop = 0; // Siempre empieza desde arriba
        }
        document.body.style.overflow = 'hidden'; // Bloquea el body detrás
        setTimeout(() => {
          if (modalInput) modalInput.focus();
        }, 150);
      }
    }
  };

  // Close handler
  const closeSearch = () => {
    if (modal) modal.classList.remove('is-open');
    if (modalOverlay) modalOverlay.classList.remove('is-active');
    if (drawer) drawer.classList.remove('is-open');
    if (drawerOverlay) drawerOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
  };

  // Bind trigger click events
  triggers.forEach(trigger => {
    trigger.addEventListener('click', openSearch);
  });

  // Bind hamburger menu search trigger, if any exists in mobile nav
  const hamburguerSearchTrigger = document.querySelector('.hamburger-search-trigger');
  if (hamburguerSearchTrigger) {
    hamburguerSearchTrigger.addEventListener('click', openSearch);
  }

  // Close events
  if (modalCloseBtn) modalCloseBtn.addEventListener('click', closeSearch);
  if (modalOverlay) {
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        closeSearch();
      }
    });
  }
  if (drawerCloseBtn) drawerCloseBtn.addEventListener('click', closeSearch);
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) {
        closeSearch();
      }
    });
  }
  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', (e) => {
      if (e.target === drawerOverlay) {
        closeSearch();
      }
    });
  }

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSearch();
  });

  const bindPredictiveSearch = (options) => {
    const {
      input,
      resultsContainer,
      defaultContent,
      loadingSpinner,
      limit,
      defaultDisplay,
      resultsWrapperClass,
      emptyClass,
      suggestionsTitleClass,
      suggestionsListStyle,
      aspectStyle
    } = options;

    if (!input || !resultsContainer || !defaultContent || !loadingSpinner) return;

    let debounceTimeout;
    let activeRequestId = 0;

    input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(debounceTimeout);
      activeRequestId += 1;
      const requestId = activeRequestId;

      if (query.length < 2) {
        resultsContainer.innerHTML = '';
        resultsContainer.style.display = 'none';
        defaultContent.style.display = defaultDisplay;
        loadingSpinner.style.display = 'none';
        return;
      }

      loadingSpinner.style.display = 'flex';
      defaultContent.style.display = 'none';
      resultsContainer.style.display = 'block';

      debounceTimeout = window.setTimeout(async () => {
        try {
          const searchData = await fetchPredictiveProductResults(query, limit);
          if (requestId !== activeRequestId) return;

          const products = searchData.products || [];
          const queries = searchData.queries || [];

          if (products.length === 0) {
            loadingSpinner.style.display = 'none';
            resultsContainer.innerHTML = buildSearchEmptyStateMarkup(query, queries, {
              emptyClass,
              bodyClass: '',
              showIcon: false,
              showTitle: false,
              bodyText: `No se encontraron productos para "<strong>${escapeSearchHtml(query)}</strong>".`,
              suggestionsOptions: {
                containerClass: 'search-suggestions-container',
                titleClass: `search-suggestions-title ${suggestionsTitleClass}`,
                listClass: 'search-suggestions-list',
                titleText: 'Sugerencias de búsqueda',
                containerStyle: 'text-align: left;',
                listStyle: suggestionsListStyle
              }
            });
            return;
          }

          const cardsHtml = await renderPredictiveProductCards(products, { aspectStyle });
          if (requestId !== activeRequestId) return;

          loadingSpinner.style.display = 'none';
          resultsContainer.innerHTML = `<div class="${resultsWrapperClass}">${cardsHtml}</div>`;
          revealDynamicSearchContent(resultsContainer);

          if (typeof initProductCards === 'function') initProductCards();
          if (typeof initCartAjaxHandlers === 'function') initCartAjaxHandlers();
        } catch (error) {
          if (requestId !== activeRequestId) return;

          console.error('Error fetching predictive search:', error);
          loadingSpinner.style.display = 'none';
          resultsContainer.innerHTML = `
            <div class="${emptyClass}">
              <p>No pudimos cargar los productos en este momento.</p>
            </div>`;
        }
      }, 500);
    });
  };

  bindPredictiveSearch({
    input: drawerInput,
    resultsContainer: document.getElementById('SearchDrawerResults'),
    defaultContent: drawer.querySelector('.search-drawer-default-content'),
    loadingSpinner: drawer.querySelector('.search-drawer-loading'),
    limit: 5,
    defaultDisplay: 'flex',
    resultsWrapperClass: 'search-drawer-results-list',
    emptyClass: 'search-drawer-no-results',
    suggestionsTitleClass: 'search-drawer-section-title',
    suggestionsListStyle: 'display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem;',
    aspectStyle: ''
  });

  bindPredictiveSearch({
    input: modalInput,
    resultsContainer: document.getElementById('SearchModalResults'),
    defaultContent: modal ? modal.querySelector('.search-modal-default-content') : null,
    loadingSpinner: modal ? modal.querySelector('.search-modal-loading') : null,
    limit: 8,
    defaultDisplay: 'block',
    resultsWrapperClass: 'search-modal-results-grid',
    emptyClass: 'search-modal-no-results',
    suggestionsTitleClass: 'search-modal-section-title',
    suggestionsListStyle: 'display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem;',
    aspectStyle: modal ? (modal.getAttribute('data-card-aspect-ratio') || '3 / 4') : ''
  });
}

const PRODUCT_SEARCH_STOP_WORDS = new Set([
  'a', 'al', 'algo', 'alguna', 'alguno', 'algun', 'alli', 'and', 'con', 'como', 'cual', 'cuales',
  'de', 'del', 'donde', 'el', 'ella', 'ellas', 'ellos', 'en', 'es', 'esta', 'este', 'esto',
  'for', 'hay', 'i', 'la', 'las', 'le', 'les', 'lo', 'los', 'me', 'mi', 'mis', 'necesito',
  'of', 'para', 'por', 'que', 'quiero', 'se', 'sin', 'su', 'sus', 'the', 'to', 'tu', 'un',
  'una', 'uno', 'unos', 'unas', 'y'
]);

function getShopRootPath() {
  const rootUrl = window.Shopify?.routes?.root || '/';
  return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
}

function escapeSearchHtml(str = '') {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function highlightSearchMatch(text, query) {
  if (!query) return escapeSearchHtml(text);

  const sourceText = String(text || '');
  const queryClean = String(query).toLowerCase();
  const index = sourceText.toLowerCase().indexOf(queryClean);

  if (index === -1) return escapeSearchHtml(sourceText);

  const before = sourceText.substring(0, index);
  const match = sourceText.substring(index, index + query.length);
  const after = sourceText.substring(index + query.length);

  return `${escapeSearchHtml(before)}<strong style="color: var(--color-search-highlight, var(--color-link)); font-weight: var(--font-weight-semibold);">${escapeSearchHtml(match)}</strong>${escapeSearchHtml(after)}`;
}

function normalizeProductSearchText(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildProductSearchCandidates(text) {
  const rawQuery = String(text || '').trim().replace(/\s+/g, ' ');
  const normalizedTokens = normalizeProductSearchText(rawQuery).split(' ').filter(Boolean);
  const filteredTokens = [];

  normalizedTokens.forEach((token) => {
    const isNumericToken = /^\d+$/.test(token);
    if (!isNumericToken && token.length < 2) return;
    if (PRODUCT_SEARCH_STOP_WORDS.has(token)) return;
    if (!filteredTokens.includes(token)) {
      filteredTokens.push(token);
    }
  });

  const candidates = [];
  const pushCandidate = (candidate) => {
    const cleanCandidate = String(candidate || '').trim().replace(/\s+/g, ' ');
    if (cleanCandidate.length < 2) return;
    if (!candidates.some((existing) => existing.toLowerCase() === cleanCandidate.toLowerCase())) {
      candidates.push(cleanCandidate);
    }
  };

  pushCandidate(rawQuery);

  if (filteredTokens.length) {
    pushCandidate(filteredTokens.join(' '));
    if (filteredTokens.length > 3) {
      pushCandidate(filteredTokens.slice(0, 4).join(' '));
      pushCandidate(filteredTokens.slice(-3).join(' '));
    } else if (filteredTokens.length > 1) {
      pushCandidate(filteredTokens.slice(-2).join(' '));
    }
  }

  return candidates;
}

function ensureProductSearchUrl(urlOrQuery) {
  const rootPath = getShopRootPath();

  if (!urlOrQuery) {
    return `${rootPath}search?type=product`;
  }

  try {
    const parsedUrl = new URL(urlOrQuery, window.location.origin);
    parsedUrl.searchParams.set('type', 'product');
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch (_error) {
    return `${rootPath}search?q=${encodeURIComponent(String(urlOrQuery))}&type=product`;
  }
}

function formatSearchResultMoney(amount) {
  if (typeof amount === 'string') return amount;

  const amountNumber = Number(amount);
  if (!Number.isFinite(amountNumber)) return '';

  const currencyCode = window.theme?.currency?.code || 'USD';
  const currencySymbol = window.theme?.currency?.symbol || '$';
  const locale = document.documentElement.lang || navigator.language || 'es';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode
    }).format(amountNumber / 100);
  } catch (_error) {
    return `${currencySymbol}${(amountNumber / 100).toFixed(2)}`;
  }
}

async function fetchPredictiveProductResults(rawQuery, limit = 8) {
  const rootPath = getShopRootPath();
  const cappedLimit = Math.max(1, Math.min(Number(limit) || 8, 10));
  const candidates = buildProductSearchCandidates(rawQuery);
  let bestFallback = {
    products: [],
    queries: [],
    matchedQuery: candidates[0] || String(rawQuery || '').trim()
  };

  for (const candidate of candidates) {
    const response = await fetch(`${rootPath}search/suggest.json?q=${encodeURIComponent(candidate)}&resources[type]=product,query&resources[limit]=${cappedLimit}&resources[limit_scope]=each`);
    if (!response.ok) {
      throw new Error(`Predictive search failed with status ${response.status}`);
    }

    const data = await response.json();
    const products = data.resources?.results?.products || [];
    const queries = data.resources?.results?.queries || [];

    if (products.length > 0) {
      return {
        products,
        queries,
        matchedQuery: candidate
      };
    }

    if (bestFallback.queries.length === 0 && queries.length > 0) {
      bestFallback = {
        products,
        queries,
        matchedQuery: candidate
      };
    }
  }

  return bestFallback;
}

async function fetchProductCardMarkup(product, options = {}) {
  const rootPath = getShopRootPath();
  const productHandle = product?.handle;
  const fallbackAspectStyle = options.aspectStyle ? ` style="--card-aspect-ratio: ${options.aspectStyle};"` : '';

  if (productHandle) {
    try {
      const response = await fetch(`${rootPath}products/${productHandle}?view=card`);
      if (response.ok) {
        return await response.text();
      }
    } catch (_error) {
      // Fallback below keeps search responsive even if the card view fails.
    }
  }

  const productTitle = product?.title || 'Producto';
  const productUrl = product?.url || '#';
  const imageUrl = product?.featured_image?.url || product?.image?.url || product?.image || product?.featured_image || '//cdn.shopify.com/s/images/themes/product-1.png';
  const priceAmount = product?.price ?? product?.price_min ?? product?.price_max;
  const compareAmount = product?.compare_at_price ?? product?.compare_at_price_min ?? product?.compare_at_price_max;
  const formattedPrice = formatSearchResultMoney(priceAmount);
  const formattedComparePrice = Number(compareAmount) > Number(priceAmount) ? formatSearchResultMoney(compareAmount) : '';

  return `
    <div class="product-card-item"${fallbackAspectStyle}>
      <div class="product-card-image-wrapper">
        <a href="${productUrl}" class="product-card-image-link">
          <img src="${imageUrl}" alt="${escapeSearchHtml(productTitle)}" class="product-card-img" loading="lazy">
        </a>
      </div>
      <div class="product-card-info align-left">
        <h4 class="product-card-title">
          <a href="${productUrl}">${escapeSearchHtml(productTitle)}</a>
        </h4>
        <div class="product-card-pricing">
          <span class="product-card-price">${formattedPrice}</span>
          ${formattedComparePrice ? `<span class="product-card-compare-price">${formattedComparePrice}</span>` : ''}
        </div>
      </div>
    </div>`;
}

async function renderPredictiveProductCards(products, options = {}) {
  const htmlArray = await Promise.all((products || []).map((product) => fetchProductCardMarkup(product, options)));
  return htmlArray.join('');
}

function revealDynamicSearchContent(scope) {
  if (!scope || !scope.querySelectorAll) return;

  scope.querySelectorAll('.reveal-on-scroll, .reveal-container-on-scroll, .zoom-in-on-scroll').forEach((element) => {
    element.classList.add('is-visible');
  });
}

function buildSearchSuggestionsHtml(queries, query, options = {}) {
  if (!Array.isArray(queries) || queries.length === 0) return '';

  const containerClass = options.containerClass || 'search-suggestions-container';
  const titleClass = options.titleClass || 'search-suggestions-title';
  const listClass = options.listClass || 'search-suggestions-list';
  const titleText = options.titleText || 'Sugerencias de búsqueda';
  const containerStyle = options.containerStyle || '';
  const listStyle = options.listStyle || '';

  return `
    <div class="${containerClass}"${containerStyle ? ` style="${containerStyle}"` : ''}>
      <h4 class="${titleClass}">${titleText}</h4>
      <div class="${listClass}"${listStyle ? ` style="${listStyle}"` : ''}>
        ${queries.map((item) => {
          const suggestionText = item?.text || item?.query || item?.styled_text || '';
          const suggestionUrl = ensureProductSearchUrl(item?.url || suggestionText);
          return `<a href="${suggestionUrl}" class="search-suggestion-pill search-keyword-pill">${highlightSearchMatch(suggestionText, query)}</a>`;
        }).join('')}
      </div>
    </div>`;
}

function buildSearchEmptyStateMarkup(query, queries, options = {}) {
  const emptyClass = options.emptyClass || 'search-results-empty';
  const bodyClass = options.bodyClass || 'search-empty-text';
  const titleClass = options.titleClass || 'search-empty-title';
  const titleText = options.titleText || 'No se encontraron resultados';
  const bodyText = options.bodyText || `No encontramos productos para "${escapeSearchHtml(query)}".`;
  const showIcon = options.showIcon !== false;
  const showTitle = options.showTitle !== false;
  const suggestionsHtml = buildSearchSuggestionsHtml(queries, query, options.suggestionsOptions || {});

  return `
    <div class="${emptyClass}">
      ${showIcon ? `
        <div class="search-empty-icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="48" height="48" style="margin: 0 auto;">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.602 10.602Z" />
          </svg>
        </div>` : ''}
      ${showTitle ? `<h2 class="${titleClass}">${titleText}</h2>` : ''}
      <p${bodyClass ? ` class="${bodyClass}"` : ''}>${bodyText}</p>
    </div>
    ${suggestionsHtml}`;
}

function initMainSearchPage() {
  const root = document.querySelector('[data-live-product-search]');
  if (!root || root.dataset.liveSearchReady === 'true') return;

  const form = root.querySelector('[data-search-form]');
  const input = root.querySelector('[data-search-input]');
  const loading = root.querySelector('[data-search-loading]');
  const info = root.querySelector('[data-search-results-info]');
  const resultsHost = root.querySelector('[data-search-results-grid]');
  const emptyState = root.querySelector('[data-search-empty]');
  const limit = Number(root.getAttribute('data-search-products-limit')) || 8;

  if (!form || !input || !loading || !info || !resultsHost || !emptyState) return;

  root.dataset.liveSearchReady = 'true';

  let debounceTimeout;
  let activeRequestId = 0;

  const updateSearchUrl = (query) => {
    if (!window.history?.replaceState) return;

    const url = new URL(window.location.href);

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    url.searchParams.set('type', 'product');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  const clearLiveResults = () => {
    loading.hidden = true;
    info.hidden = true;
    emptyState.hidden = true;
    resultsHost.innerHTML = '';
  };

  input.addEventListener('input', () => {
    const query = input.value.trim();

    clearTimeout(debounceTimeout);
    activeRequestId += 1;
    const requestId = activeRequestId;

    if (query.length < 2) {
      clearLiveResults();
      updateSearchUrl('');
      return;
    }

    loading.hidden = false;
    info.hidden = true;
    emptyState.hidden = true;

    debounceTimeout = window.setTimeout(async () => {
      try {
        const searchData = await fetchPredictiveProductResults(query, limit);
        if (requestId !== activeRequestId) return;

        const products = searchData.products || [];

        if (products.length === 0) {
          loading.hidden = true;
          info.hidden = true;
          resultsHost.innerHTML = '';
          emptyState.hidden = false;
          emptyState.innerHTML = buildSearchEmptyStateMarkup(query, searchData.queries, {
            emptyClass: 'search-results-empty',
            titleText: 'No se encontraron productos',
            bodyText: `No encontramos productos para "${escapeSearchHtml(query)}".`,
            suggestionsOptions: {
              containerClass: 'search-trending-container',
              titleClass: 'search-trending-label',
              listClass: 'search-trending-list',
              titleText: 'Prueba también',
              listStyle: 'display: flex; flex-wrap: wrap; gap: 0.6rem; justify-content: center; align-items: center; margin-top: 1rem;'
            }
          });
          updateSearchUrl(query);
          return;
        }

        const cardsHtml = await renderPredictiveProductCards(products);
        if (requestId !== activeRequestId) return;

        loading.hidden = true;
        emptyState.hidden = true;
        resultsHost.innerHTML = `
          <div class="collection-products-grid cols-4" id="CollectionProductsGrid">
            ${cardsHtml}
          </div>`;
        revealDynamicSearchContent(resultsHost);
        info.textContent = products.length === 1
          ? `Mostrando 1 producto para "${query}"`
          : `Mostrando ${products.length} productos para "${query}"`;
        info.hidden = false;
        updateSearchUrl(query);

        if (typeof initProductCards === 'function') initProductCards();
        if (typeof initCartAjaxHandlers === 'function') initCartAjaxHandlers();
      } catch (error) {
        if (requestId !== activeRequestId) return;

        console.error('Error performing live search:', error);
        loading.hidden = true;
        info.hidden = true;
        resultsHost.innerHTML = '';
        emptyState.hidden = false;
        emptyState.innerHTML = buildSearchEmptyStateMarkup(query, [], {
          emptyClass: 'search-results-empty',
          titleText: 'No pudimos completar la búsqueda',
          bodyText: 'Intenta nuevamente en unos segundos.'
        });
      }
    }, 650);
  });
}

/**
 * Initializes the premium floating Back to Top button
 */
function initBackToTop() {
  const backToTopBtn = document.getElementById('BackToTopBtn');
  if (!backToTopBtn) return;

  // Show button when scrolling down beyond 400px
  window.addEventListener('scroll', () => {
    if (window.pageYOffset > 400) {
      backToTopBtn.classList.add('is-visible');
    } else {
      backToTopBtn.classList.remove('is-visible');
    }
  });

  // Smooth scroll to top when clicked
  backToTopBtn.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

/**
 * Initializes the mobile collapsible footer accordions with scrollHeight transitions
 */
function initMobileFooterAccordions() {
  const headers = document.querySelectorAll('.footer-col-header');
  const breakpoint = 1024; // Matching the CSS media query max-width: 1024px

  const setInitialState = () => {
    const isMobile = window.innerWidth <= breakpoint;
    headers.forEach(header => {
      const content = header.nextElementSibling;
      if (!content) return;

      if (isMobile) {
        // On mobile, start collapsed unless the header has class 'is-open'
        const isOpen = header.classList.contains('is-open');
        if (isOpen) {
          content.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          header.classList.remove('is-open');
          header.setAttribute('aria-expanded', 'false');
          content.classList.remove('is-open');
          content.style.maxHeight = '0px';
        }
      } else {
        // On desktop, clean up all inline styles so CSS takes over
        header.classList.remove('is-open');
        header.removeAttribute('aria-expanded');
        content.classList.remove('is-open');
        content.style.maxHeight = '';
      }
    });
  };

  // Run initially
  setTimeout(setInitialState, 150);

  // Handle click events
  headers.forEach(header => {
    if (header.dataset.accordionInitialized) return;
    header.dataset.accordionInitialized = 'true';

    header.addEventListener('click', (e) => {
      if (window.innerWidth > breakpoint) return;
      e.preventDefault();

      const content = header.nextElementSibling;
      if (!content) return;

      const isOpen = header.classList.contains('is-open');

      if (isOpen) {
        header.classList.remove('is-open');
        header.setAttribute('aria-expanded', 'false');
        content.classList.remove('is-open');
        content.style.maxHeight = '0px';
      } else {
        header.classList.add('is-open');
        header.setAttribute('aria-expanded', 'true');
        content.classList.add('is-open');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    });
  });

  // Re-evaluate on window resize
  window.addEventListener('resize', () => {
    setInitialState();
  });
}

/**
 * Initializes interactive behaviors for premium product cards
 */
function initProductCards() {
  // 1. Wishlist click feedback
  const wishlistBtns = document.querySelectorAll('.product-card-action-btn.wishlist-btn, .list-action-btn.wishlist-btn');
  wishlistBtns.forEach(btn => {
    if (btn.dataset.wishlistInitialized) return;
    btn.dataset.wishlistInitialized = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest('.product-card-item');
      if (!card) return;

      const productData = {
        handle: card.dataset.productHandle,
        id: card.dataset.productId,
        title: card.dataset.productTitle,
        price: card.dataset.productPrice,
        compare_price: card.dataset.productComparePrice || null,
        image: card.dataset.productImage,
        url: card.dataset.productUrl,
        variant_id: card.dataset.variantId
      };

      if (window.themeWishlist) {
        window.themeWishlist.toggle(productData);
      }
    });
  });

  // 2. Compare click feedback (Deactivated but left safe)
  const compareBtns = document.querySelectorAll('.product-card-action-btn.compare-btn');
  compareBtns.forEach(btn => {
    if (btn.dataset.compareInitialized) return;
    btn.dataset.compareInitialized = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });

  // 3. Color Swatch Click Handler
  const swatchBtns = document.querySelectorAll('.product-card-item .swatch-btn');
  swatchBtns.forEach(btn => {
    if (btn.dataset.swatchInitialized) return;
    btn.dataset.swatchInitialized = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const card = btn.closest('.product-card-item');
      if (!card) return;

      // Toggle active class among siblings
      const row = btn.closest('.product-card-swatches-row');
      if (row) {
        row.querySelectorAll('.swatch-btn').forEach(s => s.classList.remove('is-active'));

        // Manage swatch-selected class to handle hover state correctly
        const firstSwatch = row.querySelector('.swatch-btn');
        if (btn === firstSwatch) {
          card.classList.remove('swatch-selected');
        } else {
          card.classList.add('swatch-selected');
        }
      }
      btn.classList.add('is-active');

      // Update image with smooth fade transition
      const imageUrl = btn.getAttribute('data-image-url');
      const img = card.querySelector('.product-card-img:not(.product-card-img--secondary)');
      if (imageUrl && img && img.src !== imageUrl) {
        img.style.opacity = '0';
        setTimeout(() => {
          img.src = imageUrl;
          if (img.srcset) {
            img.srcset = ''; // Clear srcset to force showing the new specific image
          }
          img.onload = () => {
            img.style.opacity = '';
          };
          // Fallback if onload doesn't fire
          setTimeout(() => {
            img.style.opacity = '';
          }, 300);
        }, 150);
      }

      // Update link URL with variant ID
      const variantId = btn.getAttribute('data-variant-id');
      const links = card.querySelectorAll('.product-card-image-link, .product-card-title a');
      links.forEach(link => {
        let baseHref = link.getAttribute('data-base-href') || link.getAttribute('href');
        if (!link.getAttribute('data-base-href')) {
          link.setAttribute('data-base-href', baseHref);
        }
        // Strip out existing variant parameter if any
        baseHref = baseHref.split('?')[0];
        if (variantId) {
          link.setAttribute('href', `${baseHref}?variant=${variantId}`);
        } else {
          link.setAttribute('href', baseHref);
        }
      });
    });
  });

  // 4. Card Option Selector & Variant Resolver Logic
  const cardItems = document.querySelectorAll('.product-card-item');
  cardItems.forEach(card => {
    if (card.dataset.selectorsInitialized) return;
    card.dataset.selectorsInitialized = 'true';

    let variants = [];
    let options = [];
    try {
      variants = JSON.parse(card.dataset.variants || '[]');
      options = JSON.parse(card.dataset.options || '[]');
    } catch (e) {
      // Safely ignore if card has no variants
    }

    if (variants.length === 0 || options.length === 0) return;

    let colorPos = null;
    let sizePos = null;

    options.forEach(opt => {
      const name = opt.name.toLowerCase();
      if (name === 'color' || name === 'colour') {
        colorPos = `option${opt.position}`;
      } else if (name === 'size' || name === 'talla') {
        sizePos = `option${opt.position}`;
      }
    });

    const updateSelectedVariant = () => {
      const activeColorBtn = card.querySelector('.swatch-btn.is-active');
      const activeSizeBtn = card.querySelector('.size-pill-btn.is-active');

      const selectedColor = activeColorBtn ? activeColorBtn.getAttribute('title') : null;
      const selectedSize = activeSizeBtn ? activeSizeBtn.getAttribute('data-size') : null;

      const needsColorSelection = !!colorPos;
      const needsSizeSelection = !!sizePos;

      const colorSelected = !needsColorSelection || !!selectedColor;
      const sizeSelected = !needsSizeSelection || !!selectedSize;

      const addBtns = card.querySelectorAll('.product-card-add-btn');

      if (colorSelected && sizeSelected) {
        // Find matching variant
        const matchedVariant = variants.find(variant => {
          let match = true;
          if (colorPos && variant[colorPos] !== selectedColor) match = false;
          if (sizePos && variant[sizePos] !== selectedSize) match = false;
          return match;
        });

        if (matchedVariant) {
          addBtns.forEach(btn => {
            btn.setAttribute('data-variant-id', matchedVariant.id);
            btn.classList.remove('quick-add-trigger');
            if (matchedVariant.available) {
              btn.textContent = 'Agregar al carrito';
              btn.disabled = false;
            } else {
              btn.textContent = 'Agotado';
              btn.disabled = true;
            }
          });
          card.setAttribute('data-variant-id', matchedVariant.id);
        } else {
          // Combination unavailable
          addBtns.forEach(btn => {
            btn.textContent = 'No disponible';
            btn.disabled = true;
            btn.classList.remove('quick-add-trigger');
          });
        }
      } else {
        // Selections incomplete
        addBtns.forEach(btn => {
          btn.textContent = 'Ver opciones';
          btn.disabled = false;
          btn.classList.add('quick-add-trigger');
        });
      }
    };

    // Attach click listeners to size buttons
    const sizeBtns = card.querySelectorAll('.size-pill-btn');
    sizeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const wasActive = btn.classList.contains('is-active');
        sizeBtns.forEach(b => b.classList.remove('is-active'));

        if (wasActive) {
          // Deselect size
          btn.classList.remove('is-active');
        } else {
          btn.classList.add('is-active');
        }

        updateSelectedVariant();
      });
    });

    // Hook color swatch clicks inside this card to trigger variant update
    const swatchBtns = card.querySelectorAll('.swatch-btn');
    swatchBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        setTimeout(updateSelectedVariant, 50);
      });
    });
  });
}

let automaticDiscountServerClockOffset = null;

/**
 * Hora "de la tienda" en segundos. El JSON del header trae el reloj del
 * servidor al renderizar; con esa diferencia corregimos el reloj del visitante,
 * que puede estar mal puesto y encender una campana antes de tiempo.
 */
function getAutomaticDiscountNowSeconds() {
  const local = Math.floor(Date.now() / 1000);
  return automaticDiscountServerClockOffset === null ? local : local + automaticDiscountServerClockOffset;
}

/**
 * `preview` solo llega en true desde el editor del tema, para poder ver como
 * quedan las fichas antes de que la campana arranque. En la tienda mandan
 * siempre las fechas.
 */
function isAutomaticDiscountCampaignLive(campaign, nowSeconds) {
  if (campaign.preview) return true;
  if (campaign.startsAt > 0 && nowSeconds < campaign.startsAt) return false;
  if (campaign.endsAt > 0 && nowSeconds > campaign.endsAt) return false;
  return true;
}

function getAutomaticDiscountCampaigns() {
  const dataEl = document.getElementById('HeaderAutomaticDiscountCampaignsData');
  if (!dataEl) return [];

  try {
    const parsed = JSON.parse(dataEl.textContent || '[]');

    // Formato nuevo: { server_now, campaigns: [] }. Se acepta tambien el array
    // suelto de la version anterior por si queda HTML cacheado.
    let rawCampaigns = [];
    if (Array.isArray(parsed)) {
      rawCampaigns = parsed;
    } else if (parsed && Array.isArray(parsed.campaigns)) {
      rawCampaigns = parsed.campaigns;
      const serverNow = parseInt(parsed.server_now, 10) || 0;
      if (serverNow > 0 && automaticDiscountServerClockOffset === null) {
        automaticDiscountServerClockOffset = serverNow - Math.floor(Date.now() / 1000);
      }
    }

    const nowSeconds = getAutomaticDiscountNowSeconds();

    return rawCampaigns
      .map((campaign, index) => {
        const discountPercentage = Math.max(0, Math.min(95, parseInt(campaign.discount_percentage, 10) || 0));
        const collectionHandle = typeof campaign.collection_handle === 'string'
          ? campaign.collection_handle.trim()
          : '';

        return {
          id: campaign.id || `automatic-discount-${index}`,
          title: campaign.title || '',
          applyToAllProducts: Boolean(campaign.apply_to_all_products),
          collectionHandle,
          discountPercentage,
          showSavings: campaign.show_savings !== false,
          startsAt: parseInt(campaign.starts_at, 10) || 0,
          endsAt: parseInt(campaign.ends_at, 10) || 0,
          preview: Boolean(campaign.preview),
          priority: parseInt(campaign.priority, 10) || index
        };
      })
      .filter((campaign) => {
        if (campaign.discountPercentage <= 0) return false;
        if (!campaign.applyToAllProducts && !campaign.collectionHandle) return false;
        return isAutomaticDiscountCampaignLive(campaign, nowSeconds);
      });
  } catch (error) {
    console.error('Invalid automatic discount campaign data', error);
    return [];
  }
}

function parseAutomaticDiscountCollectionHandles(rawHandles) {
  return new Set(
    String(rawHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean)
  );
}

function pickBestAutomaticDiscountCampaign(campaigns) {
  return campaigns.sort((a, b) => {
    if (b.discountPercentage !== a.discountPercentage) {
      return b.discountPercentage - a.discountPercentage;
    }

    return a.priority - b.priority;
  })[0] || null;
}

function findMatchingAutomaticDiscountCampaign(rawHandles) {
  const campaigns = getAutomaticDiscountCampaigns();
  if (!campaigns.length) return null;

  const productCollections = parseAutomaticDiscountCollectionHandles(rawHandles);
  const specificMatches = campaigns.filter((campaign) => {
    return campaign.collectionHandle && productCollections.has(campaign.collectionHandle);
  });

  if (specificMatches.length) {
    return pickBestAutomaticDiscountCampaign(specificMatches);
  }

  const globalMatches = campaigns.filter((campaign) => campaign.applyToAllProducts);
  return pickBestAutomaticDiscountCampaign(globalMatches);
}

function formatAutomaticDiscountMoney(cents) {
  const normalized = Math.max(0, Math.round(Number(cents) || 0));

  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(
      normalized,
      window.theme && window.theme.currency ? window.theme.currency.format : undefined
    );
  }

  const symbol = window.theme && window.theme.currency && window.theme.currency.symbol
    ? window.theme.currency.symbol
    : '$';
  const locale = document.documentElement.lang === 'es' ? 'es-CO' : undefined;

  return `${symbol}${(normalized / 100).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function calculateAutomaticDiscountedCents(cents, discountPercentage) {
  return Math.max(0, Math.round((Number(cents) || 0) * (100 - discountPercentage) / 100));
}

function calculateDiscountPercent(compareAtCents, priceCents) {
  if (!compareAtCents || compareAtCents <= priceCents) return 0;
  return Math.round(((compareAtCents - priceCents) * 100) / compareAtCents);
}

function buildAutomaticDiscountPriceText(mode, minCents, maxCents) {
  const normalizedMode = mode || 'single';
  const normalizedMin = Math.max(0, Math.round(Number(minCents) || 0));
  const normalizedMax = Math.max(normalizedMin, Math.round(Number(maxCents) || normalizedMin));

  if (normalizedMode === 'range' && normalizedMax > normalizedMin) {
    return `${formatAutomaticDiscountMoney(normalizedMin)} - ${formatAutomaticDiscountMoney(normalizedMax)}`;
  }

  if (normalizedMode === 'from') {
    return `Desde ${formatAutomaticDiscountMoney(normalizedMin)}`;
  }

  return formatAutomaticDiscountMoney(normalizedMin);
}

function buildAutomaticDiscountSavingsText(mode, minSavingsCents, maxSavingsCents) {
  const normalizedMode = mode || 'single';
  const normalizedMin = Math.max(0, Math.round(Number(minSavingsCents) || 0));
  const normalizedMax = Math.max(normalizedMin, Math.round(Number(maxSavingsCents) || normalizedMin));

  if (normalizedMode === 'range' && normalizedMax > normalizedMin) {
    return `Ahorras ${formatAutomaticDiscountMoney(normalizedMin)} - ${formatAutomaticDiscountMoney(normalizedMax)}`;
  }

  if (normalizedMode === 'from') {
    return `Ahorras desde ${formatAutomaticDiscountMoney(normalizedMin)}`;
  }

  return `Ahorras ${formatAutomaticDiscountMoney(normalizedMin)}`;
}

/**
 * Cada seccion pinta sus precios con sus propias clases. En vez de repetir la
 * logica del descuento por seccion, aqui se declara la forma de cada ficha.
 * Para sumar una seccion nueva basta anadir su entrada y poner en la tarjeta
 * los data-product-* que ya usa snippets/product-card.liquid.
 */
const AUTOMATIC_DISCOUNT_CARD_TYPES = [
  {
    card: '.product-card-item[data-product-collections]',
    pricing: '.product-card-pricing',
    price: '.product-card-price',
    compare: '.product-card-compare-price'
  },
  {
    card: '.eq-sel__card[data-product-collections]',
    pricing: '.eq-sel__prices',
    price: '.eq-sel__price',
    compare: '.eq-sel__compare'
  }
];

const AUTOMATIC_DISCOUNT_CARD_SELECTOR = AUTOMATIC_DISCOUNT_CARD_TYPES
  .map((type) => type.card)
  .join(', ');

function getAutomaticDiscountCardType(card) {
  if (!card || typeof card.matches !== 'function') return null;
  return AUTOMATIC_DISCOUNT_CARD_TYPES.find((type) => card.matches(type.card)) || null;
}

function ensureAutomaticDiscountSavingsElement(container) {
  let savingsEl = container.querySelector('.automatic-discount-savings');
  if (!savingsEl) {
    savingsEl = document.createElement('span');
    savingsEl.className = 'automatic-discount-savings';
    container.appendChild(savingsEl);
  }

  return savingsEl;
}

function resetAutomaticDiscountCard(card) {
  const type = getAutomaticDiscountCardType(card);
  if (!type) return;

  const originalPriceText = card.dataset.productOriginalPriceDisplay || '';
  const originalCompareText = card.dataset.productOriginalCompareDisplay || '';

  card.querySelectorAll(type.pricing).forEach((pricing) => {
    pricing.classList.remove('has-automatic-discount');

    const priceEl = pricing.querySelector(type.price);
    if (priceEl && originalPriceText) {
      priceEl.textContent = originalPriceText;
    }

    const compareEl = pricing.querySelector(type.compare);
    if (compareEl) {
      if (originalCompareText) {
        compareEl.textContent = originalCompareText;
        compareEl.style.display = '';
      } else if (compareEl.dataset.automaticDiscountGenerated === 'true') {
        compareEl.remove();
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }

    const savingsEl = pricing.querySelector('.automatic-discount-savings');
    if (savingsEl) {
      savingsEl.remove();
    }
  });

  card.dataset.productPrice = originalPriceText;
  card.dataset.productComparePrice = originalCompareText;
}

function applyAutomaticDiscountToCard(card) {
  const type = getAutomaticDiscountCardType(card);
  if (!type) return false;

  const priceMode = card.dataset.productPriceMode || 'single';
  const priceMinCents = parseInt(card.dataset.productPriceMinCents, 10) || 0;
  const priceMaxCents = Math.max(priceMinCents, parseInt(card.dataset.productPriceMaxCents, 10) || priceMinCents);
  const compareAtCents = parseInt(card.dataset.productCompareAtPriceCents, 10) || 0;
  const hasNativeCompare = compareAtCents > priceMinCents;
  const campaign = findMatchingAutomaticDiscountCampaign(card.dataset.productCollections);

  if (!campaign || hasNativeCompare || priceMinCents <= 0) {
    resetAutomaticDiscountCard(card);
    return false;
  }

  const discountedMinCents = calculateAutomaticDiscountedCents(priceMinCents, campaign.discountPercentage);
  const discountedMaxCents = calculateAutomaticDiscountedCents(priceMaxCents, campaign.discountPercentage);
  const discountedPriceText = buildAutomaticDiscountPriceText(priceMode, discountedMinCents, discountedMaxCents);
  const comparePriceText = buildAutomaticDiscountPriceText(priceMode, priceMinCents, priceMaxCents);
  const savingsText = buildAutomaticDiscountSavingsText(
    priceMode,
    priceMinCents - discountedMinCents,
    priceMaxCents - discountedMaxCents
  );

  card.querySelectorAll(type.pricing).forEach((pricing) => {
    const priceEl = pricing.querySelector(type.price);
    if (priceEl) {
      priceEl.textContent = discountedPriceText;
    }

    let compareEl = pricing.querySelector(type.compare);
    if (!compareEl) {
      compareEl = document.createElement('span');
      compareEl.className = type.compare.replace(/^\./, '');
      compareEl.dataset.automaticDiscountGenerated = 'true';
      pricing.appendChild(compareEl);
    }

    compareEl.textContent = comparePriceText;
    compareEl.style.display = '';
    pricing.classList.add('has-automatic-discount');

    const savingsEl = pricing.querySelector('.automatic-discount-savings');
    if (campaign.showSavings) {
      const ensuredSavingsEl = savingsEl || ensureAutomaticDiscountSavingsElement(pricing);
      ensuredSavingsEl.textContent = savingsText;
    } else if (savingsEl) {
      savingsEl.remove();
    }
  });

  card.dataset.productPrice = discountedPriceText;
  card.dataset.productComparePrice = comparePriceText;
  return true;
}

function getAutomaticDiscountProductRoot(element) {
  if (!element || typeof element.closest !== 'function') return element;

  return element.closest('.product-detail-page-wrapper')
    || element.closest('featured-product-component')
    || element.closest('[id^="FeaturedProduct-"]')
    || element.closest('.shopify-section')
    || element;
}

function syncAutomaticDiscountWishlist(root, priceText, compareText) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('.product-wishlist-btn, #MainProductWishlistBtn').forEach((wishlistBtn) => {
    wishlistBtn.dataset.productPrice = priceText || '';
    wishlistBtn.dataset.productComparePrice = compareText || '';
  });
}

function syncAutomaticDiscountStickyPrice(root, priceText) {
  if (!root || typeof root.querySelector !== 'function') return;

  const stickyPrice = root.querySelector('#StickyProductPrice');
  if (stickyPrice && priceText) {
    stickyPrice.textContent = priceText;
  }
}

function refreshAutomaticDiscountProductPricing(root, variant, options = {}) {
  if (!root || !variant) return false;

  const pricingEl = options.pricingEl
    || (root.matches && root.matches('.product-pricing[data-product-collections]') ? root : root.querySelector('.product-pricing[data-product-collections]'));

  if (!pricingEl) return false;

  const priceCents = Math.max(0, Math.round(Number(variant.price) || 0));
  const compareAtCents = Math.max(0, Math.round(Number(variant.compare_at_price) || 0));
  const hasNativeCompare = compareAtCents > priceCents;
  const campaign = findMatchingAutomaticDiscountCampaign(pricingEl.dataset.productCollections);
  const priceEl = pricingEl.querySelector('.product-price');
  const compareEl = pricingEl.querySelector('.product-compare-price');
  const dividerEl = pricingEl.querySelector('.product-price-divider');
  const saleBadgeEl = pricingEl.querySelector('.product-sale-badge');
  const savingsEl = pricingEl.querySelector('.automatic-discount-savings');

  pricingEl.dataset.productPriceCents = String(priceCents);
  pricingEl.dataset.productCompareAtPriceCents = String(compareAtCents);

  if (!campaign || hasNativeCompare || priceCents <= 0) {
    if (priceEl) {
      priceEl.textContent = formatAutomaticDiscountMoney(priceCents);
    }

    if (compareEl) {
      if (hasNativeCompare) {
        compareEl.textContent = formatAutomaticDiscountMoney(compareAtCents);
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }

    if (dividerEl) {
      dividerEl.style.display = hasNativeCompare ? '' : 'none';
    }

    if (saleBadgeEl) {
      if (hasNativeCompare) {
        saleBadgeEl.textContent = `-${calculateDiscountPercent(compareAtCents, priceCents)}%`;
        saleBadgeEl.style.display = '';
      } else {
        saleBadgeEl.style.display = 'none';
      }
    }

    if (savingsEl) {
      savingsEl.remove();
    }

    pricingEl.classList.remove('has-automatic-discount');
    syncAutomaticDiscountStickyPrice(root, formatAutomaticDiscountMoney(priceCents));
    syncAutomaticDiscountWishlist(
      root,
      formatAutomaticDiscountMoney(priceCents),
      hasNativeCompare ? formatAutomaticDiscountMoney(compareAtCents) : ''
    );
    return false;
  }

  const discountedCents = calculateAutomaticDiscountedCents(priceCents, campaign.discountPercentage);
  const priceText = formatAutomaticDiscountMoney(discountedCents);
  const compareText = formatAutomaticDiscountMoney(priceCents);

  if (priceEl) {
    priceEl.textContent = priceText;
  }

  if (compareEl) {
    compareEl.textContent = compareText;
    compareEl.style.display = '';
  }

  if (dividerEl) {
    dividerEl.style.display = '';
  }

  if (saleBadgeEl) {
    saleBadgeEl.textContent = `-${campaign.discountPercentage}%`;
    saleBadgeEl.style.display = '';
  }

  if (campaign.showSavings) {
    const ensuredSavingsEl = savingsEl || ensureAutomaticDiscountSavingsElement(pricingEl);
    ensuredSavingsEl.textContent = `Ahorras ${formatAutomaticDiscountMoney(priceCents - discountedCents)}`;
  } else if (savingsEl) {
    savingsEl.remove();
  }

  pricingEl.classList.add('has-automatic-discount');
  syncAutomaticDiscountStickyPrice(root, priceText);
  syncAutomaticDiscountWishlist(root, priceText, compareText);
  return true;
}

function initAutomaticDiscountDisplays(scope = document) {
  const productCards = [];
  const productPricingBlocks = [];

  if (scope.matches && scope.matches(AUTOMATIC_DISCOUNT_CARD_SELECTOR)) {
    productCards.push(scope);
  }
  if (scope.querySelectorAll) {
    productCards.push(...scope.querySelectorAll(AUTOMATIC_DISCOUNT_CARD_SELECTOR));
  }

  if (scope.matches && scope.matches('.product-pricing[data-product-collections]')) {
    productPricingBlocks.push(scope);
  }
  if (scope.querySelectorAll) {
    productPricingBlocks.push(...scope.querySelectorAll('.product-pricing[data-product-collections]'));
  }

  productCards.forEach((card) => applyAutomaticDiscountToCard(card));
  productPricingBlocks.forEach((pricingEl) => {
    const root = getAutomaticDiscountProductRoot(pricingEl);
    refreshAutomaticDiscountProductPricing(root, {
      price: parseInt(pricingEl.dataset.productPriceCents, 10) || 0,
      compare_at_price: parseInt(pricingEl.dataset.productCompareAtPriceCents, 10) || 0
    }, { pricingEl });
  });
}

let automaticDiscountScheduleTimer = null;
let automaticDiscountActiveSignature = null;

function getAutomaticDiscountActiveSignature() {
  return getAutomaticDiscountCampaigns()
    .map((campaign) => `${campaign.id}:${campaign.discountPercentage}`)
    .join('|');
}

/**
 * Una campana programada puede empezar o terminar con la pestana abierta.
 * Se revisa cada medio minuto y solo se repintan los precios si el conjunto de
 * campanas vivas cambio.
 */
function initAutomaticDiscountSchedule() {
  if (automaticDiscountScheduleTimer) return;

  automaticDiscountActiveSignature = getAutomaticDiscountActiveSignature();
  automaticDiscountScheduleTimer = window.setInterval(() => {
    const signature = getAutomaticDiscountActiveSignature();
    if (signature === automaticDiscountActiveSignature) return;

    automaticDiscountActiveSignature = signature;
    initAutomaticDiscountDisplays(document);
  }, 30000);
}

let automaticDiscountObserverInitialized = false;

function initAutomaticDiscountObserver() {
  if (automaticDiscountObserverInitialized || !window.MutationObserver || !document.body) return;
  automaticDiscountObserverInitialized = true;

  let refreshScheduled = false;
  const scheduleRefresh = () => {
    if (refreshScheduled) return;
    refreshScheduled = true;

    window.requestAnimationFrame(() => {
      refreshScheduled = false;
      initAutomaticDiscountDisplays(document);
    });
  };

  const observer = new MutationObserver((mutations) => {
    const shouldRefresh = mutations.some((mutation) => {
      return [...mutation.addedNodes].some((node) => {
        if (!(node instanceof Element)) return false;

        const watched = `#HeaderAutomaticDiscountCampaignsData, ${AUTOMATIC_DISCOUNT_CARD_SELECTOR}, .product-pricing[data-product-collections]`;

        return node.matches(watched) || Boolean(node.querySelector(watched));
      });
    });

    if (shouldRefresh) {
      scheduleRefresh();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

window.EquinoxAutomaticDiscounts = {
  refresh(scope = document) {
    initAutomaticDiscountDisplays(scope);
  },
  refreshProductPricing(root, variant) {
    return refreshAutomaticDiscountProductPricing(root, variant);
  }
};

document.addEventListener('shopify:section:load', () => initAutomaticDiscountDisplays(document));

/**
 * Helper function to show a sleek temporary toast notification
 */
function showToast(message) {
  let toast = document.querySelector('.theme-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'theme-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('is-active');

  // Reset existing timeouts
  if (window.toastTimeout) clearTimeout(window.toastTimeout);

  window.toastTimeout = setTimeout(() => {
    toast.classList.remove('is-active');
  }, 2200);
}

/**
 * Initializes interactive filters, accordions and layout grid switches for the Collection page
 */
function initCollectionFilters() {
  // 1. Sidebar Collapsible Accordions
  const filterHeaders = document.querySelectorAll('.filter-group-header');
  filterHeaders.forEach(header => {
    // Set initial maxHeight for open accordions so they transitions smoothly
    const content = header.nextElementSibling;
    if (content && !header.classList.contains('is-closed')) {
      content.style.maxHeight = content.scrollHeight + 'px';
    }

    header.addEventListener('click', (e) => {
      e.preventDefault();
      const content = header.nextElementSibling;
      const isClosed = header.classList.toggle('is-closed');
      header.setAttribute('aria-expanded', isClosed ? 'false' : 'true');

      if (content) {
        if (isClosed) {
          content.style.maxHeight = '0px';
          content.style.opacity = '0';
          content.style.marginTop = '0px';
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
          content.style.opacity = '1';
          content.style.marginTop = '0.8rem';
        }
      }
    });
  });

  // 2. Grid Column Layout Switches
  const gridSwitches = document.querySelectorAll('.grid-switch-btn');
  const productsGrid = document.getElementById('CollectionProductsGrid');

  if (productsGrid && gridSwitches.length > 0) {
    gridSwitches.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        // Remove active class from all switches
        gridSwitches.forEach(s => s.classList.remove('is-active'));

        // Add active class to clicked switch
        btn.classList.add('is-active');

        // Read columns count
        const cols = btn.getAttribute('data-cols');

        // Reset classes
        productsGrid.classList.remove('cols-1', 'cols-2', 'cols-3', 'cols-4');
        productsGrid.classList.add(`cols-${cols}`);
      });
    });

    // Automatically set default view to 2 columns on mobile devices initially
    if (window.innerWidth <= 768) {
      productsGrid.classList.remove('cols-1', 'cols-2', 'cols-3', 'cols-4');
      productsGrid.classList.add('cols-2');

      gridSwitches.forEach(s => s.classList.remove('is-active'));
      const mobileDefaultSwitch = document.querySelector('.grid-switch-btn.cols-2');
      if (mobileDefaultSwitch) {
        mobileDefaultSwitch.classList.add('is-active');
      }
    }
  }

  // 3. Custom Premium Sorting Dropdown Selector
  const customSelect = document.getElementById('CustomSortDropdown');
  if (customSelect) {
    const trigger = customSelect.querySelector('.custom-select-trigger');
    const label = customSelect.querySelector('.custom-select-label');
    const options = customSelect.querySelectorAll('.custom-select-option');
    const nativeSelect = document.getElementById('SortBy');

    if (trigger && options.length > 0 && nativeSelect) {
      // Toggle dropdown open/close
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = customSelect.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });

      // Handle selecting an option
      options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Remove selected class from all options
          options.forEach(opt => opt.classList.remove('is-selected'));

          // Add selected class to current option
          option.classList.add('is-selected');

          // Read value and label
          const val = option.getAttribute('data-value');
          const optionText = option.querySelector('span').textContent;

          // Update trigger label
          label.textContent = optionText;

          // Sync with hidden native select
          nativeSelect.value = val;

          // Trigger change event to fire Shopify's dynamic reactive sorting
          const event = new Event('change', { bubbles: true });
          nativeSelect.dispatchEvent(event);

          // Close custom dropdown
          customSelect.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        });
      });

      // Close dropdown if clicking anywhere else outside
      document.addEventListener('click', (e) => {
        if (!customSelect.contains(e.target)) {
          customSelect.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  // 4. Double Knob Price Slider Calculations & Synchronization
  const dualSliders = document.querySelectorAll('.dual-range-slider');
  dualSliders.forEach(slider => {
    const rangeMin = slider.querySelector('.range-min');
    const rangeMax = slider.querySelector('.range-max');
    const track = slider.querySelector('.slider-track');
    const container = slider.closest('.price-slider-container');
    if (!container) return;
    const inputMin = container.querySelector('.price-input-min');
    const inputMax = container.querySelector('.price-input-max');

    if (!rangeMin || !rangeMax || !track || !inputMin || !inputMax) return;

    const rangeMaxVal = parseFloat(rangeMin.max) || 200;

    const updateSliderUI = () => {
      let minVal = parseFloat(rangeMin.value);
      let maxVal = parseFloat(rangeMax.value);

      // Prevent thumbs from crossing each other
      if (maxVal - minVal < 0) {
        rangeMin.value = maxVal;
        minVal = maxVal;
      }

      // Sync display inputs
      inputMin.value = minVal;
      inputMax.value = maxVal;

      // Update tracks highlight width and offsets
      const leftPct = (minVal / rangeMaxVal) * 100;
      const rightPct = 100 - (maxVal / rangeMaxVal) * 100;
      track.style.left = leftPct + '%';
      track.style.right = rightPct + '%';
    };

    // Listen to changes on range handles (continuous update)
    rangeMin.addEventListener('input', updateSliderUI);
    rangeMax.addEventListener('input', updateSliderUI);

    // Initial load
    updateSliderUI();
  });

  // 5. Automatic form submission for Filter Forms
  const filtersForm = document.getElementById('CollectionFiltersForm');
  if (filtersForm) {
    // Bridge external SortBy select changes to the filter form
    const nativeSort = document.getElementById('SortBy');
    const mobileSortSelect = document.getElementById('MobileSortBy');

    if (nativeSort) {
      nativeSort.addEventListener('change', () => {
        const sortInput = filtersForm.querySelector('input[name="sort_by"]');
        if (sortInput) {
          sortInput.value = nativeSort.value;
        }
        if (mobileSortSelect) {
          mobileSortSelect.value = nativeSort.value;
        }
        filtersForm.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (mobileSortSelect) {
      mobileSortSelect.addEventListener('change', () => {
        const sortInput = filtersForm.querySelector('input[name="sort_by"]');
        if (sortInput) {
          sortInput.value = mobileSortSelect.value;
        }
        if (nativeSort) {
          nativeSort.value = mobileSortSelect.value;
          // Sync desktop custom select UI labels if present
          const customSelect = document.getElementById('CustomSortDropdown');
          if (customSelect) {
            const label = customSelect.querySelector('.custom-select-label');
            const activeOption = customSelect.querySelector(`.custom-select-option[data-value="${mobileSortSelect.value}"]`);
            if (label && activeOption) {
              label.textContent = activeOption.querySelector('span').textContent;
              customSelect.querySelectorAll('.custom-select-option').forEach(opt => opt.classList.remove('is-selected'));
              activeOption.classList.add('is-selected');
            }
          }
        }
        filtersForm.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // We listen to the "change" event! Checkboxes & standard select trigger "change"
    // Price range inputs will also trigger "change" when the user RELEASES the slider handles!
    filtersForm.addEventListener('change', () => {
      // Sync sort_by if SortBy native select exists and has a value
      const sortInput = filtersForm.querySelector('input[name="sort_by"]');
      if (nativeSort && sortInput) {
        sortInput.value = nativeSort.value;
      }

      // Create parameters object from Form data
      const formData = new FormData(filtersForm);
      const params = new URLSearchParams();

      for (const [key, val] of formData.entries()) {
        if (val) {
          params.append(key, val);
        }
      }

      // Automatically reload page via AJAX with the filters applied!
      const targetSearch = params.toString();
      const targetUrl = window.location.pathname + (targetSearch ? '?' + targetSearch : '');
      updateCollectionPage(targetUrl);
    });
  }

  // 6. Mobile Drawer Open/Close Interaction
  const mobileTrigger = document.getElementById('MobileFiltersTrigger');
  const drawerSidebar = document.querySelector('.collection-filters-sidebar');
  const drawerOverlay = document.getElementById('CollectionDrawerOverlay');
  const drawerCloseBtn = document.getElementById('MobileDrawerClose');

  if (mobileTrigger && drawerSidebar && drawerOverlay) {
    const openDrawer = () => {
      drawerSidebar.classList.add('is-open');
      drawerOverlay.classList.add('active');
      document.body.style.overflow = 'hidden'; // Prevent body scroll while viewing drawer
    };

    const closeDrawer = () => {
      drawerSidebar.classList.remove('is-open');
      drawerOverlay.classList.remove('active');
      document.body.style.overflow = ''; // Restore body scroll
    };

    mobileTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    });

    if (drawerCloseBtn) {
      drawerCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    }

    if (!drawerOverlay.dataset.overlayListenerAdded) {
      drawerOverlay.addEventListener('click', closeDrawer);
      drawerOverlay.dataset.overlayListenerAdded = 'true';
    }
  }

  // 7. AJAX Pagination Click Handlers
  const paginationLinks = document.querySelectorAll('.pagination-item');
  paginationLinks.forEach(link => {
    if (link.tagName === 'A') {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetUrl = link.getAttribute('href');
        if (targetUrl && targetUrl !== '#') {
          updateCollectionPage(targetUrl);
        }
      });
    }
  });

  // 7b. AJAX Pagination - Load More & Infinite Scroll
  const loadMoreBtn = document.querySelector('.load-more-btn');
  const infiniteScrollWrapper = document.querySelector('.collection-infinite-scroll-wrapper');

  // Con paginación infinita, el botón manual vive DENTRO del envoltorio de
  // scroll, así que este querySelector encuentra el mismo botón que se engancha
  // más abajo (línea del manualBtn). Sin la marca eqBound quedaban dos
  // manejadores sobre el mismo botón: un clic disparaba dos cargas, las dos
  // leían el mismo data-next-url y la misma página entraba repetida. Se veía
  // como productos duplicados al pulsar "Cargar más piezas".
  if (loadMoreBtn && !loadMoreBtn.dataset.eqBound) {
    loadMoreBtn.dataset.eqBound = 'true';
    loadMoreBtn.addEventListener('click', (e) => {
      e.preventDefault();
      loadNextCollectionPageAJAX(loadMoreBtn);
    });
  }

  if (infiniteScrollWrapper) {
    if (window.infiniteScrollObserver) {
      window.infiniteScrollObserver.disconnect();
    }

    // El bloque de paginación se reemplaza entero en cada carga, así que el
    // contador de tandas automáticas vive en window y no en este ámbito.
    if (typeof window.eqInfiniteAutoLoads !== 'number') window.eqInfiniteAutoLoads = 0;

    const autoLimit = parseInt(infiniteScrollWrapper.getAttribute('data-auto-load-limit'), 10) || 3;
    const manualWrapper = infiniteScrollWrapper.querySelector('.infinite-load-more');
    const spinner = infiniteScrollWrapper.querySelector('.load-more-spinner');

    if (window.eqInfiniteAutoLoads >= autoLimit) {
      // Agotadas las tandas automáticas: se apaga el observador y manda el botón.
      if (spinner) spinner.classList.add('hidden');
      if (manualWrapper) {
        manualWrapper.hidden = false;
        const manualBtn = manualWrapper.querySelector('.load-more-btn');
        if (manualBtn && !manualBtn.dataset.eqBound) {
          manualBtn.dataset.eqBound = 'true';
          manualBtn.addEventListener('click', (e) => {
            e.preventDefault();
            loadNextCollectionPageAJAX(manualBtn);
          });
        }
      }
    } else {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const nextUrl = infiniteScrollWrapper.getAttribute('data-next-url');
            if (nextUrl && !infiniteScrollWrapper.classList.contains('is-loading')) {
              window.eqInfiniteAutoLoads += 1;
              loadNextCollectionPageAJAX(infiniteScrollWrapper);
            }
          }
        });
      }, {
        rootMargin: '200px'
      });
      observer.observe(infiniteScrollWrapper);
      window.infiniteScrollObserver = observer;
    }
  }

  // 8. Global Escape Key Listener for Filters Drawer
  if (!window.filtersEscapeListenerAdded) {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        const activeSidebar = document.querySelector('.collection-filters-sidebar.is-open');
        if (activeSidebar) {
          activeSidebar.classList.remove('is-open');
          const overlay = document.getElementById('CollectionDrawerOverlay');
          if (overlay) overlay.classList.remove('active');
          document.body.style.overflow = '';
        }
      }
    });
    window.filtersEscapeListenerAdded = true;
  }
}

/**
 * Updates the collection page dynamically using AJAX
 * @param {string} targetUrl The target URL containing filter search parameters
 * @param {boolean} skipPushState Set to true to avoid pushing history state (e.g. for popstate)
 */
function updateCollectionPage(targetUrl, skipPushState = false) {
  const wrapper = document.querySelector('.collection-page-wrapper');
  if (!wrapper) return;

  // Cada nuevo filtro u ordenamiento empieza de cero: si no, tras haber bajado
  // mucho en una colección el siguiente resultado nacería ya con el botón.
  window.eqInfiniteAutoLoads = 0;

  // Visual feedback: reduce grid opacity and disable mouse interactions
  const grid = document.getElementById('CollectionProductsGrid');
  if (grid) {
    grid.style.opacity = '0.5';
    grid.style.pointerEvents = 'none';
  }

  fetch(targetUrl)
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newWrapperContent = doc.querySelector('.collection-page-wrapper');
      if (newWrapperContent) {
        const oldDrawer = document.querySelector('.collection-filters-sidebar');
        const newDrawer = doc.querySelector('.collection-filters-sidebar');
        const oldProducts = document.querySelector('.collection-products-panel');
        const newProducts = doc.querySelector('.collection-products-panel');

        // Capture scroll position of the drawer before swap
        const drawerScrollTop = oldDrawer ? oldDrawer.scrollTop : 0;

        // Save accordion states by group ID
        const accordionStates = {};
        document.querySelectorAll('.filter-group-wrapper').forEach(group => {
          const groupId = group.id;
          const header = group.querySelector('.filter-group-header');
          if (groupId && header) {
            accordionStates[groupId] = !header.classList.contains('is-closed');
          }
        });

        if (oldDrawer && newDrawer && oldProducts && newProducts) {
          // Swap inner content of filters sidebar and products panel separately
          // to preserve the outer elements, preventing transition triggers or losing open class.
          oldDrawer.innerHTML = newDrawer.innerHTML;
          oldDrawer.scrollTop = drawerScrollTop;
          oldProducts.innerHTML = newProducts.innerHTML;
        } else {
          // Fallback to swapping the entire wrapper content
          wrapper.innerHTML = newWrapperContent.innerHTML;
        }

        // Restore accordion states
        document.querySelectorAll('.filter-group-wrapper').forEach(group => {
          const groupId = group.id;
          if (groupId && groupId in accordionStates) {
            const isOpen = accordionStates[groupId];
            const header = group.querySelector('.filter-group-header');
            const content = group.querySelector('.filter-group-content');
            if (header && content) {
              if (isOpen) {
                header.classList.remove('is-closed');
                header.setAttribute('aria-expanded', 'true');
                content.style.maxHeight = '';
                content.style.opacity = '';
                content.style.marginTop = '';
              } else {
                header.classList.add('is-closed');
                header.setAttribute('aria-expanded', 'false');
                content.style.maxHeight = '0px';
                content.style.opacity = '0';
                content.style.marginTop = '0px';
              }
            }
          }
        });

        if (!skipPushState) {
          history.pushState({ path: targetUrl }, '', targetUrl);
        }

        // Re-initialize event listeners and animations for all elements inside the swapped content
        initCollectionFilters();
        initProductCards();
        initCartAjaxHandlers();
        initScrollAnimations();
      }
    })
    .catch(error => {
      console.error('Error fetching collection filters:', error);
      if (grid) {
        grid.style.opacity = '1';
        grid.style.pointerEvents = 'auto';
      }
    });
}

/**
 * Loads the next page of products asynchronously via AJAX and appends them
 * @param {HTMLElement} element Either the "Cargar más" button or the infinite scroll wrapper
 */
function loadNextCollectionPageAJAX(element) {
  const isLoadMore = element.classList.contains('load-more-btn');
  const nextUrl = element.getAttribute('data-next-url');
  if (!nextUrl) return;

  let wrapper = isLoadMore ? element.closest('.collection-load-more-wrapper') : element;
  if (!wrapper) return;

  // Candado: si ya hay una carga en curso no se lanza otra. Protege también
  // del doble clic impaciente, que traía el mismo lote dos veces.
  if (wrapper.classList.contains('is-loading')) return;

  wrapper.classList.add('is-loading');
  const spinner = wrapper.querySelector('.load-more-spinner');
  if (spinner) spinner.classList.remove('hidden');
  if (isLoadMore) element.style.display = 'none';

  fetch(nextUrl)
    .then(response => {
      if (!response.ok) throw new Error('Failed to load next page');
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Append products to grid
      const newGrid = doc.getElementById('CollectionProductsGrid');
      const currentGrid = document.getElementById('CollectionProductsGrid');
      if (newGrid && currentGrid) {
        const newProducts = Array.from(newGrid.children);
        newProducts.forEach(product => {
          currentGrid.appendChild(product);
        });
      }

      // Replace pagination element
      const oldPagination = document.getElementById('CollectionPagination');
      const newPagination = doc.getElementById('CollectionPagination');
      if (oldPagination && newPagination) {
        oldPagination.outerHTML = newPagination.outerHTML;
      } else if (oldPagination) {
        oldPagination.remove();
      }

      // Re-initialize cards, cart, and scroll animations
      initProductCards();
      initCartAjaxHandlers();
      if (typeof initScrollAnimations === 'function') initScrollAnimations();
      // Re-initialize collection filter listeners so the new pagination trigger is bound
      initCollectionFilters();
    })
    .catch(error => {
      console.error('AJAX pagination error:', error);
      wrapper.classList.remove('is-loading');
      if (spinner) spinner.classList.add('hidden');
      if (isLoadMore) element.style.display = 'inline-block';
    });
}

// Handle browser navigation (back/forward arrows)
if (!window.filtersPopstateListenerAdded) {
  window.addEventListener('popstate', () => {
    if (document.querySelector('.collection-page-wrapper')) {
      updateCollectionPage(window.location.href, true);
    }
  });
  window.filtersPopstateListenerAdded = true;
}

/**
 * Initializes the AJAX Add to Cart and Line Item removal event listeners
 */
function initCartAjaxHandlers() {
  // Select all add-to-cart buttons
  const addBtns = document.querySelectorAll('.product-card-add-btn, .product-card-add-btn-icon-always, .rec-card-add-btn-ajax, .list-action-btn.cart-btn');

  addBtns.forEach(btn => {
    // Prevent duplicate listeners on AJAX buttons
    if (btn.dataset.ajaxInitialized) return;
    btn.dataset.ajaxInitialized = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const insideSearchDrawer = btn.closest('#SearchDrawer');

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) {
        console.error('No variant ID found on add-to-cart button');
        return;
      }

      const isIconOnly = btn.classList.contains('product-card-add-btn-icon-always');
      let originalContent;

      // Loading state feedback
      if (isIconOnly) {
        originalContent = btn.innerHTML;
        btn.innerHTML = `
          <svg style="animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity: 0.25; fill: none;"></circle>
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `;
      } else {
        originalContent = btn.textContent;
        btn.textContent = 'Agregando...';
      }

      btn.disabled = true;

      // Shopify AJAX Add to Cart POST request
      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams({
          'id': variantId,
          'quantity': 1
        }).toString()
      })
        .then(res => {
          if (!res.ok) {
            throw new Error('Error adding product to cart');
          }
          return res.json();
        })
        .then(item => {
          // Success state feedback
          if (isIconOnly) {
            btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" width="20" height="20">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          `;
          } else {
            btn.textContent = '¡Añadido!';
          }

          if (insideSearchDrawer) {
            const searchDrawerCloseBtn = document.querySelector('#SearchDrawer .search-drawer-close');
            if (searchDrawerCloseBtn) {
              searchDrawerCloseBtn.click();
            }
          }

          // Dynamically refresh drawer HTML and open it or refresh cart page if on main cart page!
          if (window.refreshCartPageContent) {
            window.refreshCartPageContent();
          } else {
            refreshCartDrawer(true); // pass true to open the drawer after refreshing
          }

          // Revert button status back to normal after a short delay
          setTimeout(() => {
            if (isIconOnly) {
              btn.innerHTML = originalContent;
            } else {
              btn.textContent = originalContent;
            }
            btn.disabled = false;
          }, 1500);
        })
        .catch(err => {
          console.error('AJAX cart error:', err);
          showToast('Error al agregar al carrito. Por favor, inténtelo de nuevo.');
          if (isIconOnly) {
            btn.innerHTML = originalContent;
          } else {
            btn.textContent = originalContent;
          }
          btn.disabled = false;
        });
    });
  });

  // Handle Dynamic Line Item Removal
  const removeBtns = document.querySelectorAll('.cart-item-remove-btn');
  removeBtns.forEach(btn => {
    if (btn.dataset.ajaxInitialized) return;
    btn.dataset.ajaxInitialized = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const line = btn.getAttribute('data-line');
      if (!line) return;

      const itemRow = btn.closest('.cart-drawer-item');
      if (itemRow) {
        // Smooth collapse and slide out animation
        itemRow.style.transition = 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
        itemRow.style.opacity = '0';
        itemRow.style.transform = 'translateX(50px)';
        itemRow.style.pointerEvents = 'none';
        itemRow.style.maxHeight = itemRow.offsetHeight + 'px';
        setTimeout(() => {
          itemRow.style.maxHeight = '0px';
          itemRow.style.paddingTop = '0px';
          itemRow.style.paddingBottom = '0px';
          itemRow.style.borderBottomWidth = '0px';
          itemRow.style.marginBottom = '0px';
        }, 100);
      }

      // Fire fetch request immediately in parallel to the animation, requesting the cart-drawer section
      const fetchPromise = fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          'line': parseInt(line),
          'quantity': 0,
          'sections': 'cart-drawer'
        })
      }).then(res => {
        if (!res.ok) throw new Error('Error removing item');
        return res.json();
      });

      // Wait 300ms for the animation to complete, then process the fetch response
      setTimeout(() => {
        fetchPromise
          .then(cart => {
            refreshCartDrawer(false, cart);
            if (window.location.pathname === '/cart' && window.refreshCartPageContent) {
              window.refreshCartPageContent();
            }
          })
          .catch(err => {
            console.error('AJAX remove error:', err);
            if (itemRow) {
              itemRow.style.opacity = '';
              itemRow.style.pointerEvents = '';
              itemRow.style.transform = '';
              itemRow.style.maxHeight = '';
              itemRow.style.paddingTop = '';
              itemRow.style.paddingBottom = '';
              itemRow.style.borderBottomWidth = '';
              itemRow.style.marginBottom = '';
            }
            refreshCartDrawer(false); // Fallback refresh
          });
      }, 300);
    });
  });

  // Handle Drawer Quantity Selectors
  const drawerSelectors = document.querySelectorAll('#CartDrawer .cart-qty-selector');
  drawerSelectors.forEach(selector => {
    if (selector.dataset.qtyInitialized) return;
    selector.dataset.qtyInitialized = 'true';

    const minusBtn = selector.querySelector('.qty-minus');
    const plusBtn = selector.querySelector('.qty-plus');
    const input = selector.querySelector('.qty-input');
    if (!input) return;

    const lineIndex = input.getAttribute('data-line');

    const updateQuantity = (newVal) => {
      if (newVal < 0) newVal = 0;

      // Check inventory limits
      const invManagement = input.getAttribute('data-inventory-management');
      const invPolicy = input.getAttribute('data-inventory-policy');
      const invQuantity = parseInt(input.getAttribute('data-inventory-quantity'));

      if (invManagement && invPolicy !== 'continue' && !isNaN(invQuantity)) {
        if (newVal > invQuantity) {
          newVal = invQuantity;
          input.value = newVal;
          if (typeof showToast !== 'undefined') {
            showToast('No hay más stock disponible.');
          } else {
            alert('No hay más stock disponible.');
          }
          return;
        }
      }

      input.value = newVal;

      // Show loader
      selector.classList.add('is-loading');

      const itemRow = selector.closest('.cart-drawer-item');
      let delay = 0;
      if (newVal === 0 && itemRow) {
        delay = 300;
        // If removing, animate it out smoothly
        itemRow.style.transition = 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
        itemRow.style.opacity = '0';
        itemRow.style.transform = 'translateX(50px)';
        itemRow.style.pointerEvents = 'none';
        itemRow.style.maxHeight = itemRow.offsetHeight + 'px';
        setTimeout(() => {
          itemRow.style.maxHeight = '0px';
          itemRow.style.paddingTop = '0px';
          itemRow.style.paddingBottom = '0px';
          itemRow.style.borderBottomWidth = '0px';
          itemRow.style.marginBottom = '0px';
        }, 100);
      }

      // Fire fetch request immediately in parallel to the animation, requesting the cart-drawer section
      const fetchPromise = fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: parseInt(lineIndex),
          quantity: newVal,
          sections: 'cart-drawer'
        })
      }).then(res => {
        if (!res.ok) throw new Error('Error updating drawer cart qty');
        return res.json();
      });

      const handleResponse = () => {
        fetchPromise
          .then(cart => {
            refreshCartDrawer(false, cart);
            if (window.location.pathname === '/cart' && window.refreshCartPageContent) {
              window.refreshCartPageContent();
            }
          })
          .catch(err => {
            console.error('AJAX qty update error:', err);
            refreshCartDrawer(false);
          });
      };

      if (delay > 0) {
        setTimeout(handleResponse, delay);
      } else {
        handleResponse();
      }
    };

    if (minusBtn) {
      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentVal = parseInt(input.value) || 0;
        if (currentVal > 0) {
          updateQuantity(currentVal - 1);
        }
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentVal = parseInt(input.value) || 0;
        updateQuantity(currentVal + 1);
      });
    }

    input.addEventListener('change', () => {
      const newVal = parseInt(input.value);
      if (!isNaN(newVal)) {
        updateQuantity(newVal);
      }
    });
  });
}

/**
 * Dynamically refreshes the Shopify Cart Drawer section via the Section Rendering API
 */
function refreshCartDrawer(openDrawerAfter = false, preRenderedHtml = null) {
  const sectionId = 'cart-drawer';
  const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';

  const currentDrawer = document.getElementById('CartDrawer');

  // If opening requested, open immediately to start the CSS slide-in animation on stable DOM
  if (openDrawerAfter && currentDrawer) {
    currentDrawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const recPanel = document.getElementById('CartDrawerRecs');
    if (recPanel) recPanel.classList.remove('is-closed');
  }

  const wasOpen = currentDrawer && currentDrawer.classList.contains('is-open');

  const oldSection = document.getElementById('shopify-section-cart-drawer');
  if (oldSection && wasOpen) {
    oldSection.classList.add('is-updating');
  }

  const handleJson = (json) => {
    const html = json[sectionId] || (json.sections && json.sections[sectionId]);
    if (!html) {
      if (oldSection) oldSection.classList.remove('is-updating');
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newSection = doc.getElementById('shopify-section-cart-drawer') || doc.querySelector('.shopify-section');

    if (newSection && oldSection) {
      const oldDrawer = document.getElementById('CartDrawer');
      const oldRecs = document.getElementById('CartDrawerRecs');
      const oldMain = document.getElementById('CartDrawerMain');
      const oldWrapper = oldSection.querySelector('.cart-drawer-wrapper');

      const newDrawer = newSection.querySelector('#CartDrawer');
      const newRecs = newSection.querySelector('#CartDrawerRecs');
      const newMain = newSection.querySelector('#CartDrawerMain');
      const newWrapper = newSection.querySelector('.cart-drawer-wrapper');

      const canUpdateInPlace = oldDrawer && newDrawer && oldRecs && newRecs && oldMain && newMain && oldWrapper && newWrapper;

      if (canUpdateInPlace) {
        // Capture active tab state before update
        const activeTab = oldDrawer.querySelector('.cart-drawer-tab-btn.is-active');
        const activeTabId = activeTab ? activeTab.getAttribute('data-tab') : null;

        // Swap panel contents in place to preserve parent DOM nodes (transition properties remain stable)
        oldRecs.innerHTML = newRecs.innerHTML;
        oldMain.innerHTML = newMain.innerHTML;

        // Sync outer wrapper classes (color scheme, empty state)
        oldWrapper.className = newWrapper.className;

        // Sync discount attribute
        if (newDrawer.hasAttribute('data-active-discount')) {
          oldDrawer.setAttribute('data-active-discount', newDrawer.getAttribute('data-active-discount'));
        } else {
          oldDrawer.removeAttribute('data-active-discount');
        }

        // Restore active tab
        if (activeTabId) {
          const restoredTab = oldMain.querySelector(`.cart-drawer-tab-btn[data-tab="${activeTabId}"]`);
          const restoredPanel = oldMain.querySelector(`#AddonPanel-${activeTabId}`);
          if (restoredTab) restoredTab.classList.add('is-active');
          if (restoredPanel) restoredPanel.classList.add('is-active');
        }

        // Re-initialize drawer interactivity
        initCartDrawer();
        initCartAjaxHandlers();
        initCartCoupon();
        updateCartBadges();

        // Ensure visual classes are in correct state
        if (wasOpen || openDrawerAfter) {
          oldDrawer.classList.add('is-open');
        }
      } else {
        // Fallback to full swap if elements are missing
        const activeTab = oldDrawer ? oldDrawer.querySelector('.cart-drawer-tab-btn.is-active') : null;
        const activeTabId = activeTab ? activeTab.getAttribute('data-tab') : null;

        if (wasOpen || openDrawerAfter) {
          if (newDrawer) newDrawer.classList.add('is-open');
        }

        if (activeTabId && newSection) {
          const newTab = newSection.querySelector(`.cart-drawer-tab-btn[data-tab="${activeTabId}"]`);
          const newPanel = newSection.querySelector(`#AddonPanel-${activeTabId}`);
          if (newTab) newTab.classList.add('is-active');
          if (newPanel) newPanel.classList.add('is-active');
        }

        oldSection.innerHTML = newSection.innerHTML;

        initCartDrawer();
        initCartAjaxHandlers();
        initCartCoupon();
        updateCartBadges();

        const restoredDrawer = document.getElementById('CartDrawer');
        if (restoredDrawer) {
          if (wasOpen || openDrawerAfter) {
            requestAnimationFrame(() => {
              restoredDrawer.classList.add('is-open');
              document.body.style.overflow = 'hidden';
              const recPanel = document.getElementById('CartDrawerRecs');
              if (recPanel) recPanel.classList.remove('is-closed');
            });
          }
        }
      }

      // Fade back in smoothly
      setTimeout(() => {
        oldSection.classList.remove('is-updating');
      }, 50);
    } else {
      if (oldSection) oldSection.classList.remove('is-updating');
    }
  };

  if (preRenderedHtml) {
    handleJson(preRenderedHtml);
  } else {
    fetch(`${rootUrl}?sections=${sectionId}`)
      .then(res => res.json())
      .then(json => {
        handleJson(json);
      })
      .catch(err => {
        console.error('Error refreshing cart drawer section:', err);
        if (oldSection) oldSection.classList.remove('is-updating');
      });
  }
}

/**
 * Fetches the current cart state and updates all header and sticky badge counts dynamically
 */
function updateCartBadges() {
  const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';

  fetch(rootUrl + 'cart.js')
    .then(res => res.json())
    .then(cart => {
      const count = cart.item_count;

      const headerBadges = document.querySelectorAll('.cart-badge, .cart-count-badge, #CartCount');
      const stickyBadges = document.querySelectorAll('#MobileStickyCartCount');

      headerBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });

      stickyBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });
    })
    .catch(err => {
      console.error('Error updating cart badges:', err);
    });
}

function showTemporaryBanner(msgContainer, savedCoupon, isDrawer) {
  if (!msgContainer) return;

  if (msgContainer.fadeTimeout) clearTimeout(msgContainer.fadeTimeout);
  if (msgContainer.hideTimeout) clearTimeout(msgContainer.hideTimeout);

  const styleMargin = isDrawer ? 'margin-bottom: 12px;' : 'margin-top: 10px;';
  msgContainer.innerHTML = `
    <div class="coupon-activated-banner" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; ${styleMargin} padding: 10px 14px; background-color: var(--color-success-background); border: 1px solid color-mix(in srgb, var(--color-success) 35%, transparent); border-radius: max(0px, calc(var(--layout-border-radius) - 12px)); transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1); transform: translateY(-10px); opacity: 0;">
      <span style="display: flex; align-items: center; gap: 6px; color: var(--color-success); font-size: var(--font-size-sm); font-weight: var(--font-body-weight-medium);">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" width="16" height="16" style="flex-shrink: 0;">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Cupón <strong>"${savedCoupon}"</strong> activado
      </span>
      <button type="button" class="clear-coupon-btn" style="background: none; border: none; color: var(--color-link); cursor: pointer; font-size: var(--font-size-xs); font-weight: var(--font-button-weight); padding: 2px 6px; text-decoration: underline; transition: color 0.2s;" onmouseover="this.style.color='var(--color-link-hover)'" onmouseout="this.style.color='var(--color-link)'">Quitar</button>
    </div>
  `;
  msgContainer.style.display = 'block';

  const banner = msgContainer.querySelector('.coupon-activated-banner');
  if (banner) {
    void banner.offsetWidth;
    banner.style.opacity = '1';
    banner.style.transform = 'translateY(0)';

    msgContainer.fadeTimeout = setTimeout(() => {
      banner.style.opacity = '0';
      banner.style.transform = 'translateY(-10px)';
      msgContainer.hideTimeout = setTimeout(() => {
        msgContainer.style.display = 'none';
        msgContainer.innerHTML = '';
      }, 500);
    }, 4000);
  }
}

function updateCouponUI() {
  const drawerEl = document.getElementById('CartDrawer');
  const pageEl = document.querySelector('.cart-page-wrapper');

  let serverDiscount = null;
  if (drawerEl && drawerEl.hasAttribute('data-active-discount')) {
    serverDiscount = drawerEl.getAttribute('data-active-discount') || '';
  } else if (pageEl && pageEl.hasAttribute('data-active-discount')) {
    serverDiscount = pageEl.getAttribute('data-active-discount') || '';
  }

  if (serverDiscount !== null) {
    const currentSessionCoupon = sessionStorage.getItem('cart_discount_code') || '';
    if (!serverDiscount && currentSessionCoupon) {
      sessionStorage.removeItem('cart_discount_code');
      sessionStorage.removeItem('last_shown_coupon');
    } else if (serverDiscount && serverDiscount !== currentSessionCoupon) {
      sessionStorage.setItem('cart_discount_code', serverDiscount);
    }
  }

  const savedCoupon = sessionStorage.getItem('cart_discount_code') || '';
  const lastShownCoupon = sessionStorage.getItem('last_shown_coupon') || '';
  const drawerCoupon = document.getElementById('CartCoupon');
  const pageCoupon = document.getElementById('CartPageCoupon');
  const drawerMsg = document.getElementById('CartDrawerCouponMessage');
  const pageMsg = document.getElementById('CartPageCouponMessage');

  // Sync inputs
  if (drawerCoupon) drawerCoupon.value = savedCoupon;
  if (pageCoupon) pageCoupon.value = savedCoupon;

  // Only show temporary confirmation banner if the coupon is newly activated
  if (savedCoupon !== lastShownCoupon) {
    if (savedCoupon) {
      if (drawerMsg) showTemporaryBanner(drawerMsg, savedCoupon, true);
      if (pageMsg) showTemporaryBanner(pageMsg, savedCoupon, false);
      sessionStorage.setItem('last_shown_coupon', savedCoupon);
    } else {
      sessionStorage.removeItem('last_shown_coupon');
      if (drawerMsg) {
        drawerMsg.style.display = 'none';
        drawerMsg.innerHTML = '';
      }
      if (pageMsg) {
        pageMsg.style.display = 'none';
        pageMsg.innerHTML = '';
      }
    }
  }

  // Sync applied coupon badges on cart and drawer products
  const badges = document.querySelectorAll('.applied-coupon-badge');
  badges.forEach(badge => {
    if (savedCoupon) {
      const codePlaceholder = badge.querySelector('.coupon-code-placeholder');
      if (codePlaceholder) codePlaceholder.textContent = savedCoupon;
      badge.style.display = 'inline-flex';
      badge.style.opacity = '0';
      void badge.offsetWidth;
      badge.style.opacity = '1';
    } else {
      badge.style.opacity = '0';
      setTimeout(() => {
        badge.style.display = 'none';
      }, 300);
    }
  });
}

/**
 * Restores and synchronizes discount code inputs across drawer and cart page,
 * persisting active coupons in sessionStorage and showing alerts.
 */
function initCartCoupon() {
  const drawerCoupon = document.getElementById('CartCoupon');
  const pageCoupon = document.getElementById('CartPageCoupon');

  // Synchronize inputs and status banners
  updateCouponUI();

  // Bind drawer coupon Save button
  const drawerSaveBtn = document.querySelector('#AddonPanel-coupon .overlay-btn-save');
  if (drawerSaveBtn && !drawerSaveBtn.dataset.couponBound) {
    drawerSaveBtn.dataset.couponBound = 'true';
    drawerSaveBtn.addEventListener('click', () => {
      if (drawerCoupon) {
        const val = drawerCoupon.value.trim();
        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        if (val) {
          sessionStorage.setItem('cart_discount_code', val);
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: val })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón activado: ' + val);
              updateCouponUI();
              if (window.refreshCartDrawer) window.refreshCartDrawer(false);
            });
        } else {
          sessionStorage.removeItem('cart_discount_code');
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: '' })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón eliminado');
              updateCouponUI();
              if (window.refreshCartDrawer) window.refreshCartDrawer(false);
            });
        }
      }
    });
  }

  // Bind page coupon Apply button
  const pageApplyBtn = document.querySelector('.coupon-apply-btn');
  if (pageApplyBtn && !pageApplyBtn.dataset.couponBound) {
    pageApplyBtn.dataset.couponBound = 'true';
    pageApplyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (pageCoupon) {
        const val = pageCoupon.value.trim();
        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        if (val) {
          sessionStorage.setItem('cart_discount_code', val);
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: val })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón activado: ' + val);
              updateCouponUI();
              if (window.refreshCartPageContent) window.refreshCartPageContent();
            });
        } else {
          sessionStorage.removeItem('cart_discount_code');
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: '' })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón eliminado');
              updateCouponUI();
              if (window.refreshCartPageContent) window.refreshCartPageContent();
            });
        }
      }
    });
  }

  // Add Enter key handlers for coupon inputs
  if (pageCoupon && !pageCoupon.dataset.enterBound) {
    pageCoupon.dataset.enterBound = 'true';
    pageCoupon.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (pageApplyBtn) pageApplyBtn.click();
      }
    });
  }

  if (drawerCoupon && !drawerCoupon.dataset.enterBound) {
    drawerCoupon.dataset.enterBound = 'true';
    drawerCoupon.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (drawerSaveBtn) drawerSaveBtn.click();
      }
    });
  }

  // Bind remove coupon button in summary
  const removeSummaryBtns = document.querySelectorAll('.remove-coupon-btn-summary');
  removeSummaryBtns.forEach(removeSummaryBtn => {
    if (removeSummaryBtn && !removeSummaryBtn.dataset.removeBound) {
      removeSummaryBtn.dataset.removeBound = 'true';
      removeSummaryBtn.addEventListener('click', (e) => {
        e.preventDefault();
        sessionStorage.removeItem('cart_discount_code');
        sessionStorage.removeItem('last_shown_coupon');
        const drawerCouponInput = document.getElementById('CartCoupon');
        const pageCouponInput = document.getElementById('CartPageCoupon');
        if (drawerCouponInput) drawerCouponInput.value = '';
        if (pageCouponInput) pageCouponInput.value = '';

        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        fetch(rootUrl + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ discount: '' })
        })
          .then(res => res.json())
          .then(() => {
            showToast('Cupón eliminado');
            updateCouponUI();
            if (window.refreshCartPageContent) window.refreshCartPageContent();
            if (window.refreshCartDrawer) window.refreshCartDrawer(false);
          });
      });
    }
  });
}

// Global handler to clear coupon when clicking the banner "Quitar" button
document.addEventListener('click', (e) => {
  if (e.target && e.target.classList.contains('clear-coupon-btn')) {
    e.preventDefault();
    sessionStorage.removeItem('cart_discount_code');
    sessionStorage.removeItem('last_shown_coupon');
    const drawerCoupon = document.getElementById('CartCoupon');
    const pageCoupon = document.getElementById('CartPageCoupon');
    if (drawerCoupon) drawerCoupon.value = '';
    if (pageCoupon) pageCoupon.value = '';

    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    fetch(rootUrl + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ discount: '' })
    })
      .then(res => res.json())
      .then(() => {
        updateCouponUI();
        showToast('Cupón eliminado');
        if (window.refreshCartPageContent) {
          window.refreshCartPageContent();
        } else if (window.refreshCartDrawer) {
          window.refreshCartDrawer(false);
        }
      });
  }
});

// Intercept checkout form submissions to append the discount code
document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!form) return;

  const actionAttr = form.getAttribute('action');
  if (typeof actionAttr === 'string') {
    // Only intercept checkout or cart summary forms. NEVER modify product add to cart, update, or change actions
    const isCartAddUpdateOrChange = actionAttr.includes('/cart/add') || actionAttr.includes('/cart/update') || actionAttr.includes('/cart/change');
    const isCheckoutOrCartPage = actionAttr.includes('/checkout') || actionAttr.includes('/cart');

    if (isCheckoutOrCartPage && !isCartAddUpdateOrChange) {
      const drawerCoupon = document.getElementById('CartCoupon');
      const pageCoupon = document.getElementById('CartPageCoupon');

      let discountVal = '';
      // Prefer active input values in the DOM, fallback to sessionStorage
      if (drawerCoupon) {
        discountVal = drawerCoupon.value.trim();
      } else if (pageCoupon) {
        discountVal = pageCoupon.value.trim();
      } else {
        discountVal = sessionStorage.getItem('cart_discount_code') || '';
      }

      if (discountVal) {
        sessionStorage.setItem('cart_discount_code', discountVal);
        try {
          const actionUrl = new URL(actionAttr, window.location.origin);
          actionUrl.searchParams.set('discount', discountVal);
          form.setAttribute('action', actionUrl.pathname + actionUrl.search);
        } catch (err) {
          console.error('Error modifying form action:', err);
        }
      } else {
        sessionStorage.removeItem('cart_discount_code');
        try {
          const actionUrl = new URL(actionAttr, window.location.origin);
          if (actionUrl.searchParams.has('discount')) {
            actionUrl.searchParams.delete('discount');
            form.setAttribute('action', actionUrl.pathname + actionUrl.search);
          }
        } catch (err) {
          console.error('Error modifying form action:', err);
        }
      }
    }
  }
});



// Expose functions to the global window scope so other scripts/components can use them
window.refreshCartDrawer = refreshCartDrawer;
window.updateCartBadges = updateCartBadges;
window.initCartAjaxHandlers = initCartAjaxHandlers;
window.initProductCards = initProductCards;
window.initCartCoupon = initCartCoupon;

/**
 * Initializes the main product page buy buttons (AJAX for Agregar al carrito)
 */
function initProductPageBuyButtons() {
  const form = document.querySelector('form[id^="product-form-"]') || document.getElementById('ProductForm');
  if (!form) return;

  const addToCartBtn = document.getElementById('ProductAddToCartBtn');
  const stickyForm = document.getElementById('sticky-product-form');
  const stickySubmitBtn = document.getElementById('StickySubmitBtn');

  // Helper function to submit product via AJAX and open the drawer
  const addProductViaAjax = (variantId, quantity, btn) => {
    if (!variantId) {
      console.error('No variant ID found for addition');
      return;
    }

    const isSticky = btn === stickySubmitBtn;
    const btnTextSpan = isSticky ? btn.querySelector('.sticky-btn-text') : btn.querySelector('.btn-text');
    if (!btnTextSpan) return;

    const originalContent = btnTextSpan.innerHTML;

    if (isSticky) {
      btnTextSpan.textContent = 'Añadiendo...';
    } else {
      btnTextSpan.textContent = 'Añadiendo...';
    }
    btn.disabled = true;

    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';

    // Check if any addon checkboxes are checked
    const checkedAddons = document.querySelectorAll('.product-addon-checkbox:checked');
    // Opcion "Es regalo" (bloque _product-gift-option): viaja como propiedades
    // de linea del producto principal, nunca del complemento.
    const giftProperties = typeof window.eqxGetGiftProperties === 'function' ? window.eqxGetGiftProperties() : null;
    let requestBody;
    let contentType;

    if (checkedAddons.length > 0) {
      let items = [
        {
          id: parseInt(variantId),
          quantity: parseInt(quantity)
        }
      ];
      if (giftProperties) {
        items[0].properties = giftProperties;
      }
      checkedAddons.forEach(checkbox => {
        const card = checkbox.closest('.product-addon-card');
        if (card) {
          const variantInput = card.querySelector('input[type="hidden"]');
          if (variantInput && variantInput.value) {
            items.push({
              id: parseInt(variantInput.value),
              quantity: 1
            });
          }
        }
      });
      requestBody = JSON.stringify({ items: items });
      contentType = 'application/json';
    } else {
      const params = new URLSearchParams({
        'id': variantId,
        'quantity': quantity
      });
      if (giftProperties) {
        Object.keys(giftProperties).forEach(key => {
          params.append(`properties[${key}]`, giftProperties[key]);
        });
      }
      requestBody = params.toString();
      contentType = 'application/x-www-form-urlencoded';
    }

    fetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': contentType,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: requestBody
    })
      .then(res => {
        if (!res.ok) throw new Error('AJAX Product Page Add fail');
        return res.json();
      })
      .then(item => {
        btnTextSpan.textContent = '¡Añadido!';

        // Refresh and open global theme cart drawer, or refresh cart page if we're on the main cart page!
        if (window.refreshCartPageContent) {
          window.refreshCartPageContent();
        } else if (window.refreshCartDrawer) {
          window.refreshCartDrawer(true);
        }

        setTimeout(() => {
          if (isSticky) {
            // Re-trigger the variant sync update to restore original price/label
            const priceDisplay = document.getElementById('StickyProductPrice');
            const formattedPrice = priceDisplay ? priceDisplay.textContent : '';
            btnTextSpan.innerHTML = `Agregar al carrito - <span class="sticky-btn-price">${formattedPrice}</span>`;
          } else {
            btnTextSpan.textContent = 'Agregar al carrito';
          }
          btn.disabled = false;
        }, 1500);
      })
      .catch(err => {
        console.error(err);
        if (window.showToast) {
          window.showToast('Error al agregar al carrito. Por favor, inténtelo de nuevo.');
        } else {
          alert('Error al agregar al carrito. Por favor, inténtelo de nuevo.');
        }

        if (isSticky) {
          const priceDisplay = document.getElementById('StickyProductPrice');
          const formattedPrice = priceDisplay ? priceDisplay.textContent : '';
          btnTextSpan.innerHTML = `Agregar al carrito - <span class="sticky-btn-price">${formattedPrice}</span>`;
        } else {
          btnTextSpan.textContent = 'Agregar al carrito';
        }
        btn.disabled = false;
      });
  };

  // Helper to detect if any bundle app (like Fast Bundle) is active on the page
  const isBundleActive = () => {
    return window.FastBundle ||
      window.fastBundle ||
      window.rbrBundle ||
      window.rbrBundleBuilder ||
      window.RBR ||
      document.querySelector('.rbrBundleBuilder, [class*="fast-bundle"], [class*="fast_bundle"], [class*="rbr"], [class*="bundle-builder"], .fast_bundle_container');
  };

  // Helper to detect if the request payload or form content contains bundle parameters
  const isBundleRequest = (body) => {
    if (!body) return false;
    let bodyStr = "";
    if (body instanceof FormData) {
      for (let [key, val] of body.entries()) {
        bodyStr += key + "=" + val + "&";
      }
    } else if (body instanceof URLSearchParams) {
      bodyStr = body.toString();
    } else if (typeof body === 'string') {
      bodyStr = body;
    } else {
      try {
        bodyStr = JSON.stringify(body);
      } catch (e) {
        bodyStr = "";
      }
    }
    return bodyStr.includes('__bundle') ||
      bodyStr.includes('bundle_id') ||
      bodyStr.includes('fast-bundle-') ||
      bodyStr.includes('properties[_');
  };

  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', (e) => {
      // If Fast Bundle is active on the page AND the product form contains bundle parameters, bypass custom AJAX submission
      if (isBundleActive() && isBundleRequest(form ? new FormData(form) : null)) {
        return;
      }
      e.preventDefault();
      const variantIdInput = document.getElementById('SelectedVariantId');
      const qtyInput = document.getElementById('ProductQtyInput');

      const variantId = variantIdInput ? variantIdInput.value : '';
      const quantity = qtyInput ? qtyInput.value : '1';

      addProductViaAjax(variantId, quantity, addToCartBtn);
    });
  }

  if (stickySubmitBtn && stickyForm) {
    stickyForm.addEventListener('submit', (e) => {
      if (isBundleActive() && isBundleRequest(new FormData(stickyForm))) {
        return;
      }
      e.preventDefault();
    });
    stickySubmitBtn.addEventListener('click', (e) => {
      // If Fast Bundle is active on the page AND the sticky form contains bundle parameters, bypass custom AJAX submission
      if (isBundleActive() && isBundleRequest(new FormData(stickyForm))) {
        return;
      }
      e.preventDefault();
      const variantIdInput = document.getElementById('StickyVariantId');
      const qtyInput = document.getElementById('StickyQtyHidden');

      const variantId = variantIdInput ? variantIdInput.value : '';
      const quantity = qtyInput ? qtyInput.value : '1';

      addProductViaAjax(variantId, quantity, stickySubmitBtn);
    });
  }

}

/**
 * Global Wishlist Manager with LocalStorage Persistence
 */
const themeWishlist = {
  get() {
    try {
      return JSON.parse(localStorage.getItem('theme_wishlist')) || [];
    } catch (e) {
      return [];
    }
  },

  save(list) {
    localStorage.setItem('theme_wishlist', JSON.stringify(list));
    this.updateUI();
  },

  has(handle) {
    if (!handle) return false;
    const list = this.get();
    return list.some(item => item.handle === handle);
  },

  toggle(productData) {
    if (!productData || !productData.handle) return false;
    let list = this.get();
    const index = list.findIndex(item => item.handle === productData.handle);
    let added = false;

    if (index === -1) {
      list.push(productData);
      added = true;
      showToast('¡Agregado a la lista de deseos!');
    } else {
      list.splice(index, 1);
      showToast('¡Eliminado de la lista de deseos!');
    }

    this.save(list);
    return added;
  },

  remove(handle) {
    if (!handle) return;
    let list = this.get();
    list = list.filter(item => item.handle !== handle);
    this.save(list);
  },

  updateUI() {
    const list = this.get();
    const count = list.length;

    // Update count badges
    const badgeDesktop = document.getElementById('WishlistCount');
    const badgeMobile = document.getElementById('MobileWishlistCount');
    const badgeHamburger = document.getElementById('HamburgerWishlistCount');

    if (badgeDesktop) {
      badgeDesktop.textContent = count;
      badgeDesktop.style.display = count > 0 ? 'flex' : 'none';
    }
    if (badgeMobile) {
      badgeMobile.textContent = count;
      badgeMobile.style.display = count > 0 ? 'flex' : 'none';
    }
    if (badgeHamburger) {
      badgeHamburger.textContent = count;
      badgeHamburger.style.display = count > 0 ? 'inline-flex' : 'none';
    }

    // Update card buttons
    document.querySelectorAll('.product-card-item').forEach(card => {
      const handle = card.dataset.productHandle;
      if (!handle) return;

      const btn = card.querySelector('.wishlist-btn');
      if (!btn) return;

      const icon = btn.querySelector('svg');
      const isActive = this.has(handle);

      if (isActive) {
        btn.classList.add('is-active');
        btn.style.backgroundColor = 'var(--color-button-background)';
        btn.style.color = 'var(--color-button-text)';
        if (icon) icon.setAttribute('fill', 'currentColor');
      } else {
        btn.classList.remove('is-active');
        btn.style.backgroundColor = '';
        btn.style.color = '';
        if (icon) icon.setAttribute('fill', 'none');
      }
    });

    // Update main product details page buttons
    const mainButtons = document.querySelectorAll('.product-wishlist-btn, #MainProductWishlistBtn');
    mainButtons.forEach(mainBtn => {
      const handle = mainBtn.dataset.productHandle;
      const isActive = this.has(handle);
      const icon = mainBtn.querySelector('svg');

      if (isActive) {
        mainBtn.classList.add('is-active');
        if (icon) icon.setAttribute('fill', 'currentColor');
      } else {
        mainBtn.classList.remove('is-active');
        if (icon) icon.setAttribute('fill', 'none');
      }
    });

    // Render wishlist drawer list
    const drawerWrapper = document.querySelector('.wishlist-drawer-wrapper');
    const drawerList = document.getElementById('WishlistDrawerItems');
    if (drawerWrapper && drawerList) {
      if (count === 0) {
        drawerWrapper.classList.add('is-wishlist-empty');
        drawerList.innerHTML = '';
      } else {
        drawerWrapper.classList.remove('is-wishlist-empty');
        drawerList.innerHTML = list.map(item => `
          <div class="wishlist-drawer-item" data-product-handle="${item.handle}">
            <div class="wishlist-item-image-box">
              <a href="${item.url || '#'}">
                <img src="${item.image || '//cdn.shopify.com/s/images/themes/product-1.png'}" alt="${item.title}" class="wishlist-item-img" loading="lazy">
              </a>
            </div>
            <div class="wishlist-item-details-box">
              <h3 class="wishlist-item-name">
                <a href="${item.url || '#'}">${item.title}</a>
              </h3>
              <div class="wishlist-item-price-val">
                ${item.price}
                ${item.compare_price ? `<span class="wishlist-item-compare-val">${item.compare_price}</span>` : ''}
              </div>
              <div class="wishlist-item-actions">
                <button type="button" class="wishlist-item-add-to-cart" data-variant-id="${item.variant_id || ''}" data-product-handle="${item.handle}">
                  Agregar al carrito
                </button>
                <button type="button" class="wishlist-item-remove" data-product-handle="${item.handle}">
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        `).join('');

        // Rebind events for newly created drawer elements
        this.bindDrawerActions();
      }
    }
  },

  bindDrawerActions() {
    const drawerList = document.getElementById('WishlistDrawerItems');
    if (!drawerList) return;

    // 1. Remove item button
    drawerList.querySelectorAll('.wishlist-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.dataset.productHandle;
        this.remove(handle);
      });
    });

    // 2. Add to cart button
    drawerList.querySelectorAll('.wishlist-item-add-to-cart').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        // Show loading spinner/text
        const originalText = btn.textContent;
        btn.textContent = 'Agregando...';
        btn.disabled = true;

        // Add to Cart via dynamic AJAX API request
        const formData = {
          items: [{
            id: variantId,
            quantity: 1
          }]
        };

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })
          .then(response => {
            if (!response.ok) {
              throw new Error('Error adding to cart');
            }
            return response.json();
          })
          .then(data => {
            btn.textContent = '¡Agregado!';
            showToast('¡Producto añadido al carrito!');

            // Refresh the global Cart Drawer if available
            if (window.refreshCartDrawer) {
              window.refreshCartDrawer(true);
            } else {
              window.location.href = '/cart';
            }

            // Close wishlist drawer
            const wishlistDrawer = document.getElementById('WishlistDrawer');
            if (wishlistDrawer) {
              wishlistDrawer.classList.remove('is-open');
            }

            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          })
          .catch(error => {
            console.error('Error adding to cart:', error);
            btn.textContent = 'Error';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          });
      });
    });
  }
};
window.themeWishlist = themeWishlist;

/**
 * Initializes interactive behaviors for the Wishlist Drawer
 */
function initWishlistDrawer() {
  const wishlistDrawer = document.getElementById('WishlistDrawer');
  const wishlistClose = document.querySelector('.wishlist-drawer-close');
  const wishlistOverlay = document.querySelector('.wishlist-drawer-overlay');

  if (!wishlistDrawer) return;

  // Open Wishlist Drawer
  const triggers = document.querySelectorAll('#HeaderWishlistTrigger, #MobileWishlistTrigger, .hamburger-wishlist-trigger');
  triggers.forEach(trigger => {
    if (trigger.dataset.drawerInitialized) return;
    trigger.dataset.drawerInitialized = 'true';
    trigger.addEventListener('click', (e) => {
      e.preventDefault();

      // Close mobile navigation drawer if open
      const mobileNavDrawer = document.getElementById('MobileNavDrawer');
      const mobileDrawerOverlay = document.querySelector('.mobile-drawer-overlay');
      if (mobileNavDrawer) {
        mobileNavDrawer.classList.remove('is-open');
      }
      if (mobileDrawerOverlay) {
        mobileDrawerOverlay.classList.remove('is-active');
      }

      wishlistDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden'; // Lock background scrolling
    });
  });

  // Close Wishlist Drawer
  const closeDrawer = () => {
    wishlistDrawer.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  if (wishlistClose) wishlistClose.addEventListener('click', closeDrawer);
  if (wishlistOverlay) wishlistOverlay.addEventListener('click', closeDrawer);
}

/**
 * Initializes interactive behaviors for the Main Product Details page Wishlist button
 */
function initMainProductWishlist() {
  const mainButtons = document.querySelectorAll('.product-wishlist-btn, #MainProductWishlistBtn');
  if (mainButtons.length === 0) return;

  mainButtons.forEach(mainBtn => {
    if (mainBtn.dataset.wishlistInitialized) return;
    mainBtn.dataset.wishlistInitialized = 'true';

    mainBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Find matching selected variant ID from form input if available
      const selectedVariantIdInput = document.getElementById('SelectedVariantId');
      const activeVariantId = selectedVariantIdInput ? selectedVariantIdInput.value : mainBtn.dataset.variantId;

      const productData = {
        handle: mainBtn.dataset.productHandle,
        title: mainBtn.dataset.productTitle,
        price: mainBtn.dataset.productPrice,
        compare_price: mainBtn.dataset.productComparePrice || null,
        image: mainBtn.dataset.productImage,
        url: mainBtn.dataset.productUrl,
        variant_id: activeVariantId
      };

      if (window.themeWishlist) {
        window.themeWishlist.toggle(productData);
      }
    });
  });
}

/* ==========================================================================
   GLOBAL CART AJAX INTERCEPTOR FOR THIRD-PARTY APPS
   ========================================================================== */
(function () {
  // Helper to detect if any bundle app is active on the page
  const isBundleAppActive = () => {
    return window.FastBundle ||
      window.fastBundle ||
      window.rbrBundle ||
      window.rbrBundleBuilder ||
      window.RBR ||
      document.querySelector('.rbrBundleBuilder, [class*="fast-bundle"], [class*="fast_bundle"], [class*="rbr"], [class*="bundle-builder"], .fast_bundle_container');
  };

  const isBundleRequest = (body) => {
    if (!body) return false;
    let bodyStr = "";
    if (body instanceof FormData) {
      for (let [key, val] of body.entries()) {
        bodyStr += key + "=" + val + "&";
      }
    } else if (body instanceof URLSearchParams) {
      bodyStr = body.toString();
    } else if (typeof body === 'string') {
      bodyStr = body;
    } else {
      try {
        bodyStr = JSON.stringify(body);
      } catch (e) {
        bodyStr = "";
      }
    }
    return bodyStr.includes('__bundle') ||
      bodyStr.includes('bundle_id') ||
      bodyStr.includes('fast-bundle-') ||
      bodyStr.includes('properties[_');
  };

  const handleCartSync = (urlString) => {
    setTimeout(() => {
      if (window.location.pathname === '/cart' && window.refreshCartPageContent) {
        window.refreshCartPageContent();
      } else if (window.refreshCartDrawer) {
        const shouldOpen = urlString.includes('/cart/add');
        window.refreshCartDrawer(shouldOpen);
      }
    }, 250); // Small delay to let Shopify process database writes
  };

  // 1. Intercept Fetch API
  const originalFetch = window.fetch;
  window.fetch = function () {
    const [url, options] = arguments;

    // If Fast Bundle is active AND this is specifically a bundle request, bypass interception
    if (isBundleAppActive() && isBundleRequest(options && options.body)) {
      return originalFetch.apply(window, arguments);
    }

    const urlString = typeof url === 'string' ? url : (url instanceof URL ? url.href : (url && url.url ? url.url : ''));

    if (urlString.includes('/cart/add') || urlString.includes('/cart/change') || urlString.includes('/cart/update')) {
      return originalFetch.apply(window, arguments).then(response => {
        if (response.ok) {
          handleCartSync(urlString);
        }
        return response;
      });
    }
    return originalFetch.apply(window, arguments);
  };

  // 2. Intercept XMLHttpRequest API (jQuery / Axios fallbacks)
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._url = typeof url === 'string' ? url : (url instanceof URL ? url.href : '');
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    // If Fast Bundle is active AND this is specifically a bundle request, bypass interception
    if (isBundleAppActive() && isBundleRequest(body)) {
      return originalSend.apply(this, arguments);
    }

    this.addEventListener('load', function () {
      if (this.status >= 200 && this.status < 300) {
        const urlString = this._url || '';
        if (urlString.includes('/cart/add') || urlString.includes('/cart/change') || urlString.includes('/cart/update')) {
          handleCartSync(urlString);
        }
      }
    });

    return originalSend.apply(this, arguments);
  };
})();

// Intersection Observer for scroll animations (.reveal-on-scroll, .reveal-container-on-scroll, .zoom-in-on-scroll)
function initScrollAnimations() {
  const animationsEnabled = document.body.getAttribute('data-scroll-animations') !== 'disabled';
  const containerEnabled = document.body.getAttribute('data-container-animations') !== 'disabled';
  const zoomEnabled = document.body.getAttribute('data-zoom-animations') !== 'disabled';

  if (!animationsEnabled) {
    // If text animations are disabled, immediately make all elements visible
    document.querySelectorAll(".reveal-on-scroll, .reveal-container-on-scroll, .zoom-in-on-scroll").forEach(el => {
      el.classList.add("is-visible");
    });
    return;
  }

  if (!containerEnabled) {
    // If container animations are disabled, immediately make container elements visible
    document.querySelectorAll(".reveal-container-on-scroll").forEach(el => {
      el.classList.add("is-visible");
    });
  }

  if (!zoomEnabled) {
    // If zoom animations are disabled, immediately show zoom-in elements
    document.querySelectorAll(".zoom-in-on-scroll").forEach(el => {
      el.classList.add("is-visible");
    });
  }

  const observerOptions = {
    root: null,
    rootMargin: "0px 0px -10% 0px",
    threshold: 0.15
  };

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe text reveal elements
  document.querySelectorAll(".reveal-on-scroll").forEach(el => observer.observe(el));

  // Observe container reveal elements only if container animations are enabled
  if (containerEnabled) {
    document.querySelectorAll(".reveal-container-on-scroll").forEach(el => observer.observe(el));
  }

  // Observe zoom elements only if zoom is enabled
  if (zoomEnabled) {
    document.querySelectorAll(".zoom-in-on-scroll").forEach(el => observer.observe(el));
  }
}

document.addEventListener("DOMContentLoaded", initScrollAnimations);
document.addEventListener("shopify:section:load", initScrollAnimations);

/**
 * Intercept search form submissions to add wildcards (*) for broad/partial matching
 * and save successfully searched queries to localStorage history
 */
document.addEventListener('submit', function (e) {
  const searchForm = e.target.closest('form[action*="/search"]');
  if (searchForm) {
    const qInput = searchForm.querySelector('input[name="q"]');
    if (qInput && qInput.value.trim()) {
      const originalValue = qInput.value.trim();

      // Guardar en el historial de localStorage
      try {
        let history = JSON.parse(localStorage.getItem('theme_search_history') || '[]');
        // Eliminar si ya existe para evitar duplicados
        history = history.filter(item => item.toLowerCase() !== originalValue.toLowerCase());
        // Agregar al inicio
        history.unshift(originalValue);
        // Limitar a 5 búsquedas más recientes
        history = history.slice(0, 5);
        localStorage.setItem('theme_search_history', JSON.stringify(history));
      } catch (err) {
        console.error('Error saving search history:', err);
      }

      if (window.theme && window.theme.enableBroadSearch) {
        // Check if it already has wildcards to avoid double-appending
        if (!originalValue.includes('*')) {
          // Format query: append * to each word (word length > 1)
          const formattedValue = originalValue
            .split(/\s+/)
            .map(word => {
              if (word.length > 1 && !word.endsWith('*')) {
                return word + '*';
              }
              return word;
            })
            .join(' ');

          // Create a hidden input to submit the formatted value instead of altering visible input
          let hiddenInput = searchForm.querySelector('input[name="q"][type="hidden"]');
          if (!hiddenInput) {
            hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = 'q';
            searchForm.appendChild(hiddenInput);
          }
          hiddenInput.value = formattedValue;
          qInput.disabled = true; // disable the original input so its value isn't sent

          // Re-enable after submit so if user navigates back, they can edit it
          setTimeout(() => {
            qInput.disabled = false;
          }, 100);
        }
      }
    }
  }
});



/* ==========================================================================
   Carrusel de "Nuevos ingresos" (equinox-home-selection)

   Vivía como <script> dentro de la propia sección y no llegaba a ejecutarse,
   así que las flechas se quedaban con el hidden inicial y la clienta nunca las
   veía. Desde aquí sí corre: theme.js se carga en todas las páginas y se
   reengancha al evento de recarga de sección del editor de temas.
   ========================================================================== */
function initEquinoxSelectionCarousels(scope) {
  const raiz = scope && scope.querySelectorAll ? scope : document;
  const mq = window.matchMedia('(min-width: 750px)');

  raiz.querySelectorAll('[data-eq-sel-viewport]').forEach((viewport) => {
    if (viewport.dataset.eqSelReady === 'true') return;
    viewport.dataset.eqSelReady = 'true';

    const track = viewport.querySelector('[data-eq-sel-track]');
    const prev = viewport.querySelector('[data-eq-sel-nav="prev"]');
    const next = viewport.querySelector('[data-eq-sel-nav="next"]');
    if (!track || !prev || !next) return;

    // 2 px de margen: los anchos fraccionarios del flex-basis dejan sobrantes
    // de menos de un píxel que darían un falso positivo.
    const desborda = () => track.scrollWidth - track.clientWidth > 2;

    const paso = () => {
      const tarjeta = track.querySelector('li');
      if (!tarjeta) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return tarjeta.getBoundingClientRect().width + gap;
    };

    const refrescar = () => {
      const activo = mq.matches && viewport.hasAttribute('data-is-carousel') && desborda();
      if (!activo) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      const max = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft <= 2;
      next.hidden = track.scrollLeft >= max - 2;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -paso(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: paso(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(refrescar), { passive: true });
    window.addEventListener('resize', () => window.requestAnimationFrame(refrescar));

    // Las pestañas ocultas miden 0 mientras están en hidden: al cambiar de
    // pestaña hay que volver a medir o el panel recién mostrado no sabe si le
    // tocan flechas.
    const seccion = viewport.closest('[data-eq-selection]');
    if (seccion && !seccion.dataset.eqSelTabs) {
      seccion.dataset.eqSelTabs = 'true';
      seccion.addEventListener('click', (evento) => {
        if (!evento.target.closest('.eq-sel__tab')) return;
        window.setTimeout(() => initEquinoxSelectionCarousels(document), 200);
        window.setTimeout(refrescar, 220);
      });
    }

    refrescar();
  });
}

document.addEventListener('DOMContentLoaded', () => initEquinoxSelectionCarousels(document));
document.addEventListener('shopify:section:load', (e) => initEquinoxSelectionCarousels(e.target || document));

/* ------------------------------------------------------------------ *\
   Equinox · Confianza

   En móvil se comporta como una tira horizontal de 1.5 cards por vista.
   Aquí le añadimos autoplay suave y un loop simple: al llegar al final,
   vuelve al inicio y sigue girando. Si la clienta toca o arrastra, se pausa
   y retoma al cabo de un momento para no pelearse con el gesto manual.
\* ------------------------------------------------------------------ */
function initEquinoxTrustCarousel(scope) {
  const raiz = scope && scope.querySelectorAll ? scope : document;
  const mq = window.matchMedia('(max-width: 749px)');

  raiz.querySelectorAll('[data-eq-trust-carousel]').forEach((bloque) => {
    if (bloque.dataset.eqTrustReady === 'true') return;
    bloque.dataset.eqTrustReady = 'true';

    const track = bloque.querySelector('[data-eq-trust-track]');
    if (!track) return;

    const autoplayMs = parseInt(bloque.getAttribute('data-eq-trust-autoplay'), 10) || 3200;
    let autoplayTimer = 0;
    let resumeTimer = 0;

    const tarjetas = () => [...track.querySelectorAll('.eq-trust__item')];

    const paso = () => {
      const tarjeta = tarjetas()[0];
      if (!tarjeta) return track.clientWidth;
      return tarjeta.getBoundingClientRect().width;
    };

    const puedeGirar = () => {
      return mq.matches
        && document.body.contains(track)
        && tarjetas().length > 1
        && track.scrollWidth - track.clientWidth > 2;
    };

    const parar = () => {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = 0;
      }
      if (resumeTimer) {
        window.clearTimeout(resumeTimer);
        resumeTimer = 0;
      }
    };

    const avanzar = () => {
      if (!puedeGirar()) return;

      const max = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft >= max - 2) {
        track.scrollTo({ left: 0, behavior: 'auto' });
        return;
      }

      const siguiente = Math.min(track.scrollLeft + paso(), max);
      track.scrollTo({ left: siguiente, behavior: 'smooth' });
    };

    const arrancar = () => {
      parar();
      if (!puedeGirar()) return;
      autoplayTimer = window.setInterval(avanzar, autoplayMs);
    };

    const pausarYRetomar = () => {
      parar();
      if (!puedeGirar()) return;
      resumeTimer = window.setTimeout(arrancar, autoplayMs + 600);
    };

    const refrescar = () => {
      if (!puedeGirar()) {
        parar();
        track.scrollTo({ left: 0, behavior: 'auto' });
        return;
      }

      if (!autoplayTimer) {
        arrancar();
      }
    };

    track.addEventListener('pointerdown', pausarYRetomar, { passive: true });
    track.addEventListener('touchstart', pausarYRetomar, { passive: true });
    track.addEventListener('mouseenter', parar);
    track.addEventListener('mouseleave', arrancar);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        parar();
      } else {
        arrancar();
      }
    });

    window.addEventListener('resize', () => window.requestAnimationFrame(refrescar));

    refrescar();
  });
}

document.addEventListener('DOMContentLoaded', () => initEquinoxTrustCarousel(document));
document.addEventListener('shopify:section:load', (e) => initEquinoxTrustCarousel(e.target || document));

/* ------------------------------------------------------------------ *\
   Equinox · Categorías con foto

   Al pasar el cursor (o tabular) por un nombre, cambia la foto grande del
   otro lado. Vive aquí y no dentro de la sección porque en este tema los
   <script> incrustados en una sección no llegan a ejecutarse.

   Se queda en la última que se señaló al salir del bloque en vez de volver a
   la primera: el salto de vuelta al mover el ratón hacia abajo se ve como un
   parpadeo y no aporta nada.
\* ------------------------------------------------------------------ */
function initEquinoxCategoryHover(scope) {
  const raiz = scope && scope.querySelectorAll ? scope : document;
  raiz.querySelectorAll('[data-eq-cathover]').forEach((bloque) => {
    if (bloque.dataset.eqCathoverListo === '1') return;
    bloque.dataset.eqCathoverListo = '1';

    const nombres = [...bloque.querySelectorAll('[data-eq-cat]')];
    const fotos = [...bloque.querySelectorAll('[data-eq-shot]')];
    const creditos = [...bloque.querySelectorAll('[data-eq-caption]')];
    if (nombres.length < 2 || !fotos.length) return;

    const activar = (i) => {
      nombres.forEach((n) => n.classList.toggle('is-active', n.dataset.eqCat === i));
      fotos.forEach((f) => f.classList.toggle('is-active', f.dataset.eqShot === i));
      creditos.forEach((c) => c.classList.toggle('is-active', c.dataset.eqCaption === i));
    };

    nombres.forEach((n) => {
      const i = n.dataset.eqCat;
      // pointerenter y no mouseenter: en un portátil táctil el dedo también
      // debe poder previsualizar sin tener que abrir la colección.
      n.addEventListener('pointerenter', () => activar(i));
      n.addEventListener('focus', () => activar(i));
    });
  });
}

document.addEventListener('DOMContentLoaded', () => initEquinoxCategoryHover(document));
document.addEventListener('shopify:section:load', (e) => initEquinoxCategoryHover(e.target || document));
